import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Drawer,
  Modal,
  Select,
  Menu,
  MenuItem,
  LinearProgress,
  Skeleton
} from '@mui/material';
import _ from 'lodash';
import { MouseEvent, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { updateSelectedId } from '../../../../redux/slices/selectedGroupSlice';
import { setSelectedValue } from '../../../../redux/slices/selectedValueSlice';
import { fetchMentorTasks } from '../../../../redux/slices/mentorTasksSlice';
import { IRoleObj } from '../../../../redux/slices/groupRolesListSlice';
import { userActions } from '../../../../redux/slices/user-profile/userProfieSlice';
import { taskActions } from '../../../../redux/slices/goals-tasks/getTasksSlice';
import { getUserDetails } from '../../../../utils/orgName';
import { getProgramCreatorFromGroupOrProgramDetails } from '../../utils/programCreatorDisplay';
import ChatAvatarComponent from '../chat/ChatAvatarComponent';
import { API } from '../../../../api';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RecruitNewMentor from './RecruitNewMentor';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { getStatusText, getStatusColor, getDisplayName } from '../../components/common';
import MeetingDetails from './MeetingDetails';
import UserProfileDetailsPopup from '../../components/UserProfileDetailsPopup';
import { navigateToChat } from '../NavigateToChat';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { buttonDisabledSx } from '../../../../components/ui/Buttons/buttonStyles';
import { ChatIcon } from '../../../../utils/Icons';
import { nanoid } from 'nanoid';
import Leaderboard from './cards/Leaderboard';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { getAcsUserByCommunicationId } from '../../../../routes/helpers';
import { toast } from 'react-toastify';
import { MemberRole, isMemberRole, useMemberWord } from '../../../../utils/memberTypeLabels';
import { FrameworkColumnsSkeleton, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { NOT_REQUIRED } from '../../../../utils/requirementsScore';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

type RoleKey = 'mentee' | 'mentor' | 'consultant' | 'unknown';
type ProgramStatusKey = 'not-started' | 'in-progress' | 'completed';

const rolePriority: RoleKey[] = ['mentee', 'mentor', 'consultant', 'unknown'];

const statusRank: Record<ProgramStatusKey, number> = {
  'not-started': 0,
  'in-progress': 1,
  completed: 2
};

interface GroupedProgram {
  id: string;
  name: string;
  roles: RoleKey[];
  active: boolean;
  entries: IRoleObj[];
  status: ProgramStatusKey;
  model?: string;
  details?: any;
  raw?: any;
}

function getItemStatus(item: any): ProgramStatusKey {
  const rawStatus = (item?.status || item?.programStatus || item?.progressStatus || '')
    .toString()
    .toLowerCase()
    .replace(/_/g, '-');

  // API values: not_started, in_progress, completed (normalized to use hyphen)
  if (
    rawStatus === 'completed' ||
    rawStatus.includes('complete') ||
    rawStatus.includes('done') ||
    rawStatus.includes('finished')
  ) {
    return 'completed';
  }
  if (rawStatus === 'not-started' || rawStatus.includes('not')) {
    return 'not-started';
  }
  if (rawStatus === 'in-progress' || rawStatus.includes('progress')) {
    return 'in-progress';
  }

  // fallback para active / completed flags
  if (item?.active === false) return 'not-started';
  if (item?.completed === true) return 'completed';

  // padrão: in-progress
  return 'in-progress';
}

const getObjectiveLookupKeys = (obj: any, idx: number) => {
  const seen = new Set<string>();
  const addKey = (value: any) => {
    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
    }
  };

  addKey(obj?.objectiveId);
  addKey(obj?.id);
  addKey(obj?._id);
  addKey(obj?.objective);
  addKey(obj?.name);
  addKey(`${obj?.objective || obj?.name || 'objective'}_${idx}`);

  return Array.from(seen);
};

const getObjectiveIdentifier = (obj: any) => {
  if (!obj) return null;
  const key = obj?.objectiveId ?? obj?.id ?? obj?._id ?? obj?.objId ?? obj?.objective ?? obj?.name;
  return key != null ? String(key) : null;
};

const isObjectiveStatusCompleted = (status: string | undefined) => {
  const normalized = (status || '').toString().toLowerCase().trim();
  return normalized === 'completed' || normalized === 'complete' || normalized === 'done' || normalized === 'finished';
};

const toSafeNumber = (value: any): number | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** Any program whose model string includes masterclass (pure, guided, hybrid) — mentee MCQ peers fetch. */
const programModelIncludesMasterclass = (model?: string) => {
  if (!model) return false;
  return model.toString().toLowerCase().includes('masterclass');
};

const flattenMenteeRowsFromPeersPayload = (payload: unknown): any[] => {
  if (payload == null) return [];
  if (Array.isArray(payload)) {
    const out: any[] = [];
    (payload as any[]).forEach((item: any) => {
      if (Array.isArray(item?.menteeDetails)) {
        out.push(...item.menteeDetails);
      } else if (item && typeof item === 'object') {
        const looksLikeMenteeRow =
          item.mid != null ||
          item.userId != null ||
          item.menteeId != null ||
          item.displayName != null ||
          item.menteeName != null;
        if (looksLikeMenteeRow) {
          out.push(item);
        }
      }
    });
    return out;
  }
  if (typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (Array.isArray(p.menteeDetails)) return p.menteeDetails as any[];
    if (Array.isArray(p.mentees)) return p.mentees as any[];
    if (Array.isArray(p.data)) return flattenMenteeRowsFromPeersPayload(p.data);
  }
  return [];
};

const pickSelfMcqScoreFromPeersPayload = (
  payload: unknown,
  selfId: string | undefined,
  communicationUserId?: string
): number | null | undefined => {
  if (!selfId && !communicationUserId) return undefined;
  const rows = flattenMenteeRowsFromPeersPayload(payload);
  const self = rows.find((m: any) => {
    const idCandidates = [m?.mid, m?.userId, m?.id, m?.menteeId, m?._id, m?.user_id].filter(
      (x: unknown) => x !== undefined && x !== null
    );
    if (selfId && idCandidates.some((x: unknown) => String(x) === String(selfId))) {
      return true;
    }
    const comm = m?.communicationUserId ?? m?.communicationId;
    if (communicationUserId && comm !== undefined && comm !== null && String(comm) === String(communicationUserId)) {
      return true;
    }
    return false;
  });
  if (!self) return undefined;
  // Null: assessed on nothing in this program (Audience Requirements, DA-5056 R5). The card reads "-".
  if (self.MCQScore === null) return null;
  return toSafeNumber(self.MCQScore ?? self.mcqScore ?? self.mcq_score);
};

/** Same sources as getCardModel — used before hook callbacks can read ref-backed getCardModel. */
const resolveProgramModelFromDetailsPayload = (details: any, cardModelFallback?: string) =>
  details?.model || details?.goals?.model || details?.goals?.programType || cardModelFallback || '';

const resolveMcqScoreFromProgramRaw = (card: GroupedProgram) => {
  const menteeEntry = card.entries?.find((e) => (e as { role?: string }).role === 'mentee') as
    | { raw?: any }
    | undefined;
  const firstEntry = card.entries?.[0] as { raw?: any } | undefined;
  const raw = menteeEntry?.raw ?? firstEntry?.raw;
  if (!raw) return undefined;
  return toSafeNumber(
    raw.MCQScore ??
      raw.mcqScore ??
      raw.insights?.MCQScore ??
      raw.insights?.mcqScore ??
      raw.insights?.mcq ??
      raw.peerInsights?.MCQScore
  );
};

/** Unresolved (red-flagged) lessons for the mentee — drives the Improve Performance CTA (L-11). */
const resolveUnresolvedLessons = (card: GroupedProgram) => {
  const menteeEntry = card.entries?.find((e) => (e as { role?: string }).role === 'mentee') as
    | { raw?: any }
    | undefined;
  const raw = menteeEntry?.raw ?? (card.entries?.[0] as { raw?: any } | undefined)?.raw;
  return toSafeNumber(raw?.unresolvedLessons) ?? 0;
};

const resolveGoalProgress = (mentee: any) => {
  if (!mentee) return 0;
  return (
    toSafeNumber(mentee?.goalProgress) ??
    toSafeNumber(mentee?.progress?.goalProgress) ??
    toSafeNumber(mentee?.progress?.progress) ??
    toSafeNumber(mentee?.progress) ??
    toSafeNumber(mentee?.progressPercentage) ??
    toSafeNumber(mentee?.progressPercent) ??
    toSafeNumber(mentee?.progressPercentile) ??
    0
  );
};

const getMenteesByObjectiveId = (mentees: any[] | undefined, objectiveId: string) => {
  if (!Array.isArray(mentees) || !objectiveId) return [];
  return mentees.filter((mentee: any) => {
    const matched = Array.isArray(mentee.matchedObjectives) ? mentee.matchedObjectives : [];
    return matched.some((obj: any) => {
      const candidateIds = [obj?.id, obj?.objectiveId, obj?._id, obj?.objective, obj?.name, obj?.objId].filter(Boolean);
      return candidateIds.some((id) => String(id) === String(objectiveId));
    });
  });
};

const DEFAULT_MENTEE_STATS = {
  mentorsEngaged: 0,
  messagesSent: 0,
  meetingsCompleted: 0,
  futureMeetings: 0
};

interface TaskSummary {
  totalLevels: number;
  greenLevels: number;
  timeRemainingMinutes: number;
}

const normalizeDrawerMenteeList = (items: any[] | undefined) => {
  if (!Array.isArray(items)) return [];
  return items.map((item: any) => ({
    ...item,
    id: item?.menteeId || item?.userId || item?.id || nanoid(),
    name: item?.menteeName || item?.displayName || item?.name || '',
    status: item?.status || item?.matchStatus || item?.matchStatusText || 'Ongoing',
    matchedObjectives: Array.isArray(item?.matchedObjectives) ? item.matchedObjectives : [],
    goalProgress: resolveGoalProgress(item)
  }));
};

const buildObjectiveMentorsMap = (entries: any[]) => {
  const map: Record<string, any[]> = {};
  (Array.isArray(entries) ? entries : []).forEach((objective: any, idx: number) => {
    const key =
      getObjectiveIdentifier(objective) ||
      objective?.id ||
      objective?._id ||
      objective?.objectiveId ||
      objective?.objective ||
      objective?.name ||
      `${objective?.objective || objective?.name || 'objective'}_${idx}`;
    if (!key) return;
    const mentors = Array.isArray(objective?.mentors) ? objective.mentors : [];
    map[key] = mentors.map((mentor: any) => ({
      ...mentor,
      matchedObjectives: [
        {
          objective: objective?.objective || objective?.name || '',
          id: key,
          objectiveId: key,
          status: objective?.status || 'Ongoing'
        }
      ],
      objectiveLabel: objective?.objective || objective?.name || '',
      objectiveStatus: objective?.status || 'Ongoing',
      objectiveId: key
    }));
  });
  return map;
};

const getMentorObjectiveTitleFromList = (mentor: any, objectives: any[]) => {
  if (!mentor || !Array.isArray(objectives) || objectives.length === 0) return '';
  const mentorId = mentor?.mid || mentor?.userId || mentor?.id || mentor?.mentorId;
  if (!mentorId) return '';
  const matchedObjective = objectives.find((objective: any) => {
    const mentorsList = Array.isArray(objective?.mentors) ? objective.mentors : [];
    return mentorsList.some((eachMentor: any) => {
      const candidateId = eachMentor?.mid || eachMentor?.userId || eachMentor?.id || eachMentor?.mentorId;
      return candidateId && candidateId === mentorId;
    });
  });
  return matchedObjective?.objective || '';
};

const sanitizeName = (value?: string) => {
  if (!value) return '';
  if (value.includes('@')) {
    return value.split('@')[0];
  }
  return value;
};

const getUserFriendlyName = (user: any) => {
  if (!user) return 'You';
  const fullNameParts = [user?.firstName, user?.lastName].filter(Boolean);
  if (fullNameParts.length) {
    return fullNameParts.join(' ');
  }
  if (user?.displayName) {
    const name = sanitizeName(user.displayName);
    if (name) return name;
  }
  if (user?.name) {
    const name = sanitizeName(user.name);
    if (name) return name;
  }
  if (user?.preferredName) {
    const name = sanitizeName(user.preferredName);
    if (name) return name;
  }
  return 'You';
};

const resolveProfileImage = (person: any, fallbackSource?: any) => {
  const source = person || {};
  const fallback = fallbackSource || {};
  const candidates = [
    source?.headshot,
    source?.headShot,
    source?.image,
    source?.imageUrl,
    source?.photoUrl,
    source?.profileImage,
    source?.avatar,
    source?.photo,
    source?.picture,
    source?.displayPicture,
    source?.profilePhoto,
    source?.profilePicture,
    source?.profilePic,
    source?.personal_details?.headshot,
    source?.personal_details?.image,
    source?.userDetails?.headshot,
    source?.userDetails?.image,
    source?.userDetails?.personal_details?.headshot,
    source?.userDetails?.personal_details?.image,
    source?.user?.headshot,
    source?.user?.headShot,
    source?.user?.image,
    source?.user?.photo,
    source?.user?.picture,
    source?.extended?.headshot,
    source?.extended?.headShot,
    source?.extended?.image,
    source?.extended?.photo,
    source?.extended?.picture,
    fallback?.headshot,
    fallback?.headShot,
    fallback?.image,
    fallback?.avatar,
    fallback?.photo,
    fallback?.picture,
    fallback?.displayPicture,
    fallback?.userDetails?.headshot,
    fallback?.userDetails?.image,
    fallback?.userDetails?.personal_details?.headshot,
    fallback?.userDetails?.personal_details?.image
  ];
  const image = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return image || undefined;
};

interface UserProgramsDashboardProps {
  // When set, the LL Member dashboard mounts this component (hidden via
  // `hidden`) just to surface its expanded program drawer for the given
  // program. Mirrors the existing `routeLocation.state.openProgramId`
  // auto-open path but bypasses navigation state.
  initialOpenProgramId?: string;
  initialOpenPreferredRole?: string;
  // When set together with `initialOpenProgramId`, the drawer opens
  // immediately using this stub instead of waiting for the dashboard's
  // own API fetch to populate `grouped`. The stub upgrades to the
  // full GroupedProgram once the fetch completes. Keeps the LL → drawer
  // handoff snappy — Continue clicks shouldn't show a global spinner.
  initialProgramStub?: Partial<GroupedProgram> & { id: string; name: string; roles: RoleKey[] };
  // When true, the dashboard body is rendered with `display: none` so
  // only the MUI Drawer (portaled to document.body) is visible. Used by
  // the LL Member dashboard to host the drawer without taking over the
  // page.
  hidden?: boolean;
}
export default function UserProgramsDashboard({
  initialOpenProgramId,
  initialOpenPreferredRole,
  initialProgramStub,
  hidden
}: UserProgramsDashboardProps = {}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const w = useMemberWord();
  // The org's own words (Settings > Members), ready for the memoised rows below.
  const menteesWord = w('mentee', { plural: true });
  const mentorsWord = w('mentor', { plural: true });
  const menteeWordLower = w('mentee', { lower: true });
  const mentorWordLower = w('mentor', { lower: true });
  // A role code as a label: "mentee"/"mentor" read as the org's word, any other code keeps its capitalised code.
  const roleLabel = (role: string) =>
    isMemberRole(role) ? w(role.trim().toLowerCase() as MemberRole) : role.charAt(0).toUpperCase() + role.slice(1);
  const groupRoleState = useAppSelector((state) => state.groupRoleList);
  const groups = groupRoleState.data || [];

  const userDetails = getUserDetails();
  const { orgId, location, communicationUserId } = userDetails;
  const currentUserId = userDetails.userId;
  const isCurrentUserMatch = useCallback(
    (person: any) => {
      if (!person) return false;
      const identifiers = [person?.userId, person?.mid, person?.id, person?.matchId, person?.mentorId, person?.menteeId]
        .filter(Boolean)
        .map((id) => String(id));
      identifiers.push(currentUserId);
      if (!currentUserId) return false;
      return identifiers.includes(String(currentUserId));
    },
    [currentUserId]
  );

  const [roleSelections, setRoleSelections] = useState<Record<string, RoleKey>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProgram, setDrawerProgram] = useState<GroupedProgram | null>(null);
  // Card the drawer is currently showing — lets async detail hydration in
  // handleOpenDrawer discard responses that arrive after the user switched cards.
  const drawerCardIdRef = useRef<string | null>(null);
  const [drawerRole, setDrawerRole] = useState<RoleKey>('mentee');
  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const personalThreadsArr = useAppSelector((state) => state.acsChannels.personalThreadsArr);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMentee, setMenuMentee] = useState<any>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [meetingMentee, setMeetingMentee] = useState<any>(null);
  const [objectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [recruitModalOpen, setRecruitModalOpen] = useState(false);
  const [recruitObjId, setRecruitObjId] = useState('');
  const [recruitObjectiveName, setRecruitObjectiveName] = useState('');
  const [learningGrpId, setLearningGrpId] = useState<string>('');
  const [objectiveMatchesMap, setObjectiveMatchesMap] = useState<Record<string, any[]>>({});
  const [programDetails, setProgramDetails] = useState<any>(null);
  const [savingObjective, setSavingObjective] = useState(false);
  const [objectivesUserCanAdd, setObjectivesUserCanAdd] = useState<any[]>([]);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState('');
  const [menteeList, setMenteeList] = useState<any[]>([]);
  const [drawerObjectiveMentors, setDrawerObjectiveMentors] = useState<Record<string, any[]>>({});
  const [existingKeys, setExistingKeys] = useState<any[]>([]);
  const [expandedObjectives, setExpandedObjectives] = useState<Record<string, boolean>>({});
  const [menteeProgressStats, setMenteeProgressStats] = useState(DEFAULT_MENTEE_STATS);
  const [menteeInsightsStats, setMenteeInsightsStats] = useState<{ xpPoints?: number }>({});
  const [drawerUserLearningObjectives, setDrawerUserLearningObjectives] = useState<any[]>([]);
  const [cardTaskSummaries, setCardTaskSummaries] = useState<Record<string, TaskSummary>>({});
  const [cardReady, setCardReady] = useState<Record<string, boolean>>({});
  const [dashboardReady, setDashboardReady] = useState(false);
  const [cardMenteeProgress, setCardMenteeProgress] = useState<Record<string, any>>({});
  const [cardMenteeMcqScores, setCardMenteeMcqScores] = useState<Record<string, number | null>>({});
  const [mentorCardStats, setMentorCardStats] = useState<
    Record<
      string,
      { messagesSent: number; menteesEngaged: number; meetingsScheduled: number; meetingsCompleted?: number }
    >
  >({});
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [leaderboardPeers, setLeaderboardPeers] = useState<any[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  // const [cardReady, setCardReady] = useState<Record<string, boolean>>({});
  const applyDrawerMenteeData = useCallback((list: any[]) => {
    setMenteeList(list);
  }, []);

  const fetchDrawerLearningObjectivesForMentee = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId) return;
      try {
        const userObjectivesRes = await API.UserLearningObjectives({ orgId });
        const userObjectivesPayload = userObjectivesRes?.data?.data || userObjectivesRes?.data || [];
        const normalizedUserObjectives = Array.isArray(userObjectivesPayload) ? userObjectivesPayload : [];
        setDrawerUserLearningObjectives(normalizedUserObjectives);
        dispatch(userActions.updateObjectives(normalizedUserObjectives));

        const response = await API.getLearningObjectives({ grpId, orgId });
        const payload = response?.data?.data || response?.data || {};
        const entries =
          payload?.learningObjectives?.objectives || payload?.learningObjectives?.data || payload?.objectives || [];
        if (!Array.isArray(entries)) {
          console.warn('Unexpected learning objectives shape', entries);
        }
        const normalizedEntries = Array.isArray(entries) ? entries : [];
        setExistingKeys(normalizedEntries);
        setDrawerObjectiveMentors(buildObjectiveMentorsMap(normalizedEntries));
      } catch (error) {
        console.error('Failed to load learning objectives for mentee drawer', error);
      }
    },
    [orgId, dispatch]
  );

  const fetchDrawerUserLearningObjectives = useCallback(async () => {
    if (!orgId) return;
    try {
      const userObjectivesRes = await API.UserLearningObjectives({ orgId });
      const userObjectivesPayload = userObjectivesRes?.data?.data || userObjectivesRes?.data || [];
      const normalizedUserObjectives = Array.isArray(userObjectivesPayload) ? userObjectivesPayload : [];
      setDrawerUserLearningObjectives(normalizedUserObjectives);
      dispatch(userActions.updateObjectives(normalizedUserObjectives));
    } catch (error) {
      console.error('Failed to load user learning objectives for mentee drawer', error);
    }
  }, [orgId, dispatch]);

  const loadLeaderboardPeers = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId || !location) return;
      setLeaderboardLoading(true);
      try {
        const response = await API.getMenteesPeersHomePage(orgId, location, grpId);
        const payload = response?.data?.data || response?.data || [];
        const entries = Array.isArray(payload) ? payload : [];
        const normalized: any[] = [];
        entries.forEach((item) => {
          if (Array.isArray(item?.menteeDetails)) {
            normalized.push(...item.menteeDetails);
          }
        });
        setLeaderboardPeers(normalized);
      } catch (error) {
        console.error('Failed to load leaderboard peers', error);
      } finally {
        setLeaderboardLoading(false);
      }
    },
    [orgId, location]
  );

  const storedTokens = (() => {
    if (typeof window === 'undefined') return {};
    const tokenString = localStorage.getItem('tokens');
    return tokenString ? JSON.parse(tokenString) : {};
  })();
  const idToken = storedTokens?.id_token;
  const accessToken = storedTokens?.access_token;
  const orgRealId = useAppSelector((state) => state.orgDetails.data?._id);
  const [apiPrograms, setApiPrograms] = useState<any[]>([]);
  const [apiProgramsLoading, setApiProgramsLoading] = useState(false);
  /** When org + tokens exist, we must not render Redux `groups` as a stand-in (stale/wrong org). Stay empty until API returns. */
  const [apiProgramsHydrated, setApiProgramsHydrated] = useState(false);
  const credentialsReady = !!(orgRealId && idToken && accessToken);
  const buildObjectiveMenteesMap = useCallback((raw: any) => {
    const objectives =
      raw?.learningObjectives?.objectives || raw?.learningObjectives?.data || raw?.learningObjectives || [];
    const list = raw?.peers?.menteePeers?.menteeDetails || [];
    const map: Record<string, any[]> = {};
    (Array.isArray(objectives) ? objectives : []).forEach((obj: any, idx: number) => {
      const key =
        getObjectiveIdentifier(obj) ||
        obj?.id ||
        obj?._id ||
        obj?.objective ||
        obj?.name ||
        `${obj?.objective || obj?.name || 'objective'}_${idx}`;
      if (key) {
        map[String(key)] = [...list];
      }
    });
    return map;
  }, []);
  const populateCardCache = useCallback(
    (items: any[]) => {
      items.forEach((item) => {
        const cardId = item.id;
        const detail = item.details;
        cardDataCacheRef.current[cardId] = {
          ...cardDataCacheRef.current[cardId],
          details: detail,
          menteeProgress: item.raw?.goalProgress || item.raw?.progress || null,
          menteeList: item.raw?.peers?.menteePeers?.menteeDetails || []
        };
      });
    },
    [buildObjectiveMenteesMap]
  );

  const flattenProgramPayload = useCallback((payload: any) => {
    const normalizedPayload = Array.isArray(payload)
      ? { menteeMasterClassDataSet: payload }
      : Array.isArray(payload?.data)
      ? { menteeMasterClassDataSet: payload.data }
      : payload;
    const datasets = [
      { key: 'menteeMasterClassDataSet', role: 'mentee' as RoleKey },
      { key: 'menteeMentorshipDataSet', role: 'mentee' as RoleKey },
      { key: 'menteeGuidedDataSet', role: 'mentee' as RoleKey },
      { key: 'mentorMasterClassDataSet', role: 'mentor' as RoleKey },
      { key: 'mentorMentorshipDataSet', role: 'mentor' as RoleKey },
      { key: 'mentorGuidedDataSet', role: 'mentor' as RoleKey }
    ];

    const entries: any[] = [];
    datasets.forEach(({ key, role }) => {
      const list = normalizedPayload?.[key] ?? [];
      (Array.isArray(list) ? list : []).forEach((item: any) => {
        const detail = (item?.groupDetails && item.groupDetails[0]) || {};
        const id = detail?.grpId || detail?.id || detail?.groupId || item?.grpId || nanoid();
        const goalProgress =
          item?.goalProgress?.goalProgress ?? item?.insights?.goalProgress ?? item?.progress?.goalProgress ?? undefined;

        // Use programStatus from API (not_started | in_progress | completed) for the status column
        const apiProgramStatus = item?.programStatus ?? detail?.programStatus;
        let status: ProgramStatusKey =
          apiProgramStatus != null && String(apiProgramStatus).trim() !== ''
            ? getItemStatus({ programStatus: apiProgramStatus, status: apiProgramStatus })
            : ('in-progress' as ProgramStatusKey);
        if (apiProgramStatus == null || String(apiProgramStatus).trim() === '') {
          if (goalProgress === 100) status = 'completed';
          else if (!goalProgress || goalProgress === 0) status = 'not-started';
        }

        entries.push({
          ...item,
          id,
          name: item?.name || detail?.name || `Program ${id}`,
          role,
          model: item?.model || detail?.model || '',
          status,
          programStatus: status,
          progressStatus: status,
          active: detail?.active ?? true,
          details: detail,
          raw: item
        });
      });
    });
    return entries;
  }, []);

  const getOrganizationsPrograms = useCallback(async () => {
    if (!orgRealId || !idToken || !accessToken) {
      setApiPrograms([]);
      setApiProgramsLoading(false);
      setApiProgramsHydrated(false);
      return;
    }
    setApiProgramsHydrated(false);
    setApiProgramsLoading(true);
    try {
      const response = await API.getUserProgramList({
        orgId: orgRealId,
        idtoken: idToken,
        authorization: accessToken
      });
      const payload = response?.data?.data || {};
      const flattened = flattenProgramPayload(payload);
      setApiPrograms(flattened);
      populateCardCache(flattened);
    } catch (error) {
      console.error('Failed to load user programs', error);
      setApiPrograms([]);
    } finally {
      setApiProgramsLoading(false);
      setApiProgramsHydrated(true);
    }
  }, [orgRealId, idToken, accessToken, flattenProgramPayload]);

  useEffect(() => {
    getOrganizationsPrograms();
  }, [getOrganizationsPrograms]);
  const cardDataCacheRef = useRef<
    Record<
      string,
      {
        details?: any;
        menteeList?: any[];
        menteeProgress?: any;
      }
    >
  >({});

  const userLo = useAppSelector((state) => state?.userProfile?.data?.learningObjectives as any);
  const [createdLearningObjectivesList, setCreatedLearningObjectivesList] = useState<any[]>([]);
  const [selectLearningObjectivesList, setSelectLearningObjectivesList] = useState<any[]>([]);
  const userLearningObjectives = useMemo(() => {
    if (!userLo) return [];
    if (Array.isArray(userLo)) return userLo;
    if (Array.isArray(userLo.objectives)) return userLo.objectives;
    return [];
  }, [userLo]);

  const getCardObjectivesInfo = (card: GroupedProgram) => {
    const cardDetails = cardDataCacheRef.current[card.id]?.details;
    const programObjectives = cardDetails?.learningObjectives?.objectives || [];
    const totalLearningObjectives =
      typeof cardDetails?.learningObjectives?.number === 'number'
        ? cardDetails.learningObjectives.number
        : programObjectives.length;

    const objectiveKeySet = new Set<string>();
    programObjectives.forEach((obj: any, idx: number) => {
      const key =
        getObjectiveIdentifier(obj) ||
        obj?.id ||
        obj?._id ||
        obj?.objectiveId ||
        `${obj?.objective || obj?.name || 'objective'}_${idx}`;
      if (key) {
        objectiveKeySet.add(key);
      }
    });

    const completedObjectivesCount =
      userLearningObjectives.reduce((count: number, userObjective: any) => {
        const key =
          getObjectiveIdentifier(userObjective) || userObjective?.id || userObjective?._id || userObjective?.objective;
        if (!key || !objectiveKeySet.has(key)) {
          return count;
        }
        return isObjectiveStatusCompleted(userObjective?.status) ? count + 1 : count;
      }, 0) ?? 0;

    const remaining = Math.max(totalLearningObjectives - completedObjectivesCount, 0);
    return {
      remainingObjectives: remaining,
      totalObjectives: totalLearningObjectives,
      hasObjectiveInfo: typeof cardDetails?.learningObjectives?.number === 'number' || programObjectives.length > 0
    };
  };

  const resolveTimeRemainingMinutesFromCard = (card: GroupedProgram | null | undefined) => {
    if (!card) return undefined;
    const entry: any = card.entries?.[0] || {};
    const cachedProgress = cardDataCacheRef.current?.[card.id]?.menteeProgress;
    const liveProgress = cardMenteeProgress?.[card.id];
    const resolved =
      toSafeNumber(entry?.goalProgress?.timeLeft) ??
      toSafeNumber(entry?.raw?.goalProgress?.timeLeft) ??
      toSafeNumber(card?.raw?.goalProgress?.timeLeft) ??
      toSafeNumber(entry?.progress?.timeLeft) ??
      toSafeNumber(entry?.raw?.progress?.timeLeft) ??
      toSafeNumber(card?.raw?.progress?.timeLeft) ??
      toSafeNumber(cachedProgress?.timeLeft) ??
      toSafeNumber(liveProgress?.timeLeft) ??
      undefined;
    return resolved;
  };

  const resolveProgramProgressFromCard = (card: GroupedProgram | null | undefined) => {
    if (!card) return undefined;
    const entry: any = card.entries?.[0] || {};
    const cachedProgress = cardDataCacheRef.current?.[card.id]?.menteeProgress;
    const liveProgress = cardMenteeProgress?.[card.id];
    return (
      toSafeNumber(entry?.goalProgress?.goalProgress) ??
      toSafeNumber(entry?.raw?.goalProgress?.goalProgress) ??
      toSafeNumber(card?.raw?.goalProgress?.goalProgress) ??
      toSafeNumber(cachedProgress?.goalProgress) ??
      toSafeNumber(liveProgress?.goalProgress) ??
      toSafeNumber(entry?.goalProgress) ??
      toSafeNumber(entry?.raw?.goalProgress) ??
      toSafeNumber(card?.raw?.goalProgress) ??
      undefined
    );
  };

  const sourceItems = useMemo(() => {
    if (!credentialsReady) {
      return groups;
    }
    if (!apiProgramsHydrated) {
      return [];
    }
    return apiPrograms;
  }, [credentialsReady, apiProgramsHydrated, apiPrograms, groups]);
  const grouped: GroupedProgram[] = useMemo(() => {
    const map = new Map<string, GroupedProgram & { hasNonAdminRole: boolean }>();

    (sourceItems || []).forEach((item: any) => {
      const key = item.id;
      const itemStatus = getItemStatus(item);

      const rawRole: string = (item.role as string) || 'unknown';
      const isAdminRole = rawRole === 'admin';

      let normalizedRole: RoleKey = 'unknown';
      if (rawRole === 'mentee' || rawRole === 'mentor' || rawRole === 'consultant') {
        normalizedRole = rawRole;
      }

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: item.name,
          roles: [],
          active: !!item.active,
          entries: [],
          status: itemStatus,
          hasNonAdminRole: !isAdminRole && normalizedRole !== 'unknown',
          model: item.model,
          details: item.details || item.groupDetails?.[0],
          raw: item.raw || item
        });
      }

      const entry = map.get(key)!;
      entry.entries.push(item);
      entry.active = entry.active && !!item.active;

      // status com prioridade (Completed > In Progress > Not Started)
      if (statusRank[itemStatus] > statusRank[entry.status]) {
        entry.status = itemStatus;
      }

      if (!isAdminRole && normalizedRole !== 'unknown' && !entry.roles.includes(normalizedRole)) {
        entry.roles.push(normalizedRole);
      }

      if (!isAdminRole && normalizedRole !== 'unknown') {
        entry.hasNonAdminRole = true;
      }
    });

    // filtra programas que só tenham role admin
    return Array.from(map.values())
      .filter((p) => p.hasNonAdminRole)
      .map(({ hasNonAdminRole, ...rest }) => rest);
  }, [sourceItems]);

  // Auto-open drawer when navigating from the Learning Lifecycle dashboard.
  // Two input channels:
  //  • Navigation state { openProgramId, preferredRole } — used by the
  //    legacy "navigate then auto-open" handoff.
  //  • Props `initialOpenProgramId` / `initialOpenPreferredRole` — used by
  //    the LL Member dashboard which mounts this component hidden (so
  //    only the MUI Drawer portal is visible) instead of taking the
  //    user away from the LL view.
  // The drawer-only re-fires whenever the prop id changes, so picking a
  // different LL card reopens for that program.
  // Auto-open the drawer in two phases:
  //   1. As soon as the LL host gives us an id + stub, open the drawer
  //      WITHOUT waiting for `grouped` to populate. The drawer's inner
  //      sections (leaderboard, tasks) handle their own loading states.
  //   2. When `grouped` later resolves, replace drawerProgram with the
  //      full GroupedProgram so downstream effects (matches, mentee
  //      lists) operate on real data instead of the stub.
  // Navigation-state callers (legacy "navigate then auto-open") still
  // wait for grouped since they have no stub.
  const lastOpenedProgramIdRef = useRef<string | null>(null);
  useEffect(() => {
    const stateFromNav = routeLocation.state as { openProgramId?: string; preferredRole?: string } | null;
    const requestedId = initialOpenProgramId || stateFromNav?.openProgramId;
    if (!requestedId) return;
    if (lastOpenedProgramIdRef.current === requestedId) return;
    const preferredRole = initialOpenPreferredRole || stateFromNav?.preferredRole;

    // Snappy path — LL host provided a stub.
    if (initialOpenProgramId && initialProgramStub && initialProgramStub.id === requestedId) {
      const stubCard: GroupedProgram = {
        id: initialProgramStub.id,
        name: initialProgramStub.name,
        roles: initialProgramStub.roles,
        active: initialProgramStub.active ?? true,
        entries: initialProgramStub.entries ?? [],
        status: initialProgramStub.status ?? 'not-started',
        model: initialProgramStub.model,
        details: initialProgramStub.details,
        raw: initialProgramStub.raw
      };
      lastOpenedProgramIdRef.current = requestedId;
      handleOpenDrawer(stubCard, preferredRole);
      return;
    }

    // Nav-state path — waits for grouped (no stub available).
    if (grouped.length === 0) return;
    const target = grouped.find((c) => c.id === requestedId);
    if (!target) return;
    lastOpenedProgramIdRef.current = requestedId;
    handleOpenDrawer(target, preferredRole);
    if (!initialOpenProgramId && stateFromNav?.openProgramId) {
      navigate(routeLocation.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, initialOpenProgramId, initialOpenPreferredRole, initialProgramStub]);

  // (Intentionally no stub → real upgrade.)  We tried swapping
  // drawerProgram from stub to the full GroupedProgram once `grouped`
  // resolved, but the identity change re-triggered the per-program
  // fetch effects inside the drawer (mentor/mentee/peer/insights),
  // producing a second loading flicker. The drawer's UI only needs
  // id + name + roles + model from the stub — every downstream fetch
  // keys off id, which is stable across both. Leaving as the stub.

  const getEffectiveStatus = (card: GroupedProgram): ProgramStatusKey => {
    const model = (getCardModel(card) || '').toLowerCase();
    const isMasterclassType = model.includes('masterclass') || model.includes('guided');

    if (isMasterclassType) {
      const summary = cardTaskSummaries[card.id];
      const displayPercent =
        summary && summary.totalLevels > 0 ? Math.round((summary.greenLevels / summary.totalLevels) * 100) : undefined;

      if (displayPercent === 0) return 'not-started';
      if (displayPercent === 100) return 'completed';

      return 'in-progress';
    }

    const hasMenteeRole = card.roles.includes('mentee');
    if (!hasMenteeRole) return card.status;

    const progressData = cardMenteeProgress[card.id] ?? cardDataCacheRef.current[card.id]?.menteeProgress ?? null;
    if (!progressData) return card.status;

    const scheduledValue =
      progressData?.meetingsScheduled ??
      progressData?.futureMeetings ??
      progressData?.scheduledMeetings ??
      progressData?.upcomingMeetings ??
      progressData?.meetings ??
      progressData?.meetingsScheduledCount ??
      progressData?.future ??
      progressData?.scheduled ??
      undefined;
    const scheduledMeetings = toSafeNumber(scheduledValue);
    const hasScheduledInfo = scheduledMeetings !== undefined;

    if (hasScheduledInfo && scheduledMeetings === 0) {
      return 'not-started';
    }

    const progressObjectivesValue =
      progressData?.objectivesPending ??
      progressData?.objectivePending ??
      progressData?.objectivesRemaining ??
      progressData?.objectivesYetToBeCompleted ??
      progressData?.remainingObjectives ??
      undefined;
    const normalizedObjectives = toSafeNumber(progressObjectivesValue);
    const hasProgressObjectivesInfo = normalizedObjectives !== undefined;

    const objectivesInfo = getCardObjectivesInfo(card);
    const hasObjectiveInfo = hasProgressObjectivesInfo || objectivesInfo.hasObjectiveInfo;
    const remainingObjectives =
      hasProgressObjectivesInfo && normalizedObjectives !== undefined
        ? normalizedObjectives
        : objectivesInfo.remainingObjectives;

    if (hasObjectiveInfo && remainingObjectives === 0) {
      return 'completed';
    }

    if (hasScheduledInfo || hasObjectiveInfo) {
      return 'in-progress';
    }

    return card.status;
  };

  const computeTaskSummaryFromPayload = (payload: any): TaskSummary | null => {
    const entries = Array.isArray(payload) ? payload : payload ? [payload] : [];
    const tasks: any[] = [];
    entries.forEach((entry) => {
      if (Array.isArray(entry?.levels)) {
        entry.levels.forEach((level: any) => {
          if (Array.isArray(level?.tasks)) {
            tasks.push(...level.tasks);
          }
        });
      }
    });
    if (!tasks.length) return null;
    const completedTasks = tasks.filter((task) => String(task?.taskColor || '').toLowerCase() === 'green').length;
    const remainingTasks = tasks.length - completedTasks;
    return {
      totalLevels: tasks.length,
      greenLevels: completedTasks,
      timeRemainingMinutes: remainingTasks * 4
    };
  };

  const shouldFetchTasksForModel = (model?: string) => {
    if (!model) return false;
    const normalized = model.toString().toLowerCase();
    return (
      normalized.includes('masterclass') || normalized.includes('mentorship&masterclass') || normalized === 'mentorship'
    );
  };

  const prefetchCardData = useCallback(
    async (card: GroupedProgram) => {
      const cardId = card.id;
      if (!cardId || !orgId || !location) return;
      setCardReady((prev) => ({ ...prev, [cardId]: false }));

      const cached = cardDataCacheRef.current[cardId] || {};
      const hasMenteeRole = card.roles.includes('mentee');

      let detailPayload = cached.details;
      if (!detailPayload) {
        try {
          const res = await API.getGroupDetails(cardId);
          detailPayload = res?.data?.data || res?.data;
          cardDataCacheRef.current[cardId] = {
            ...cardDataCacheRef.current[cardId],
            details: detailPayload
          };
        } catch {
          /* ignore */
        }
      }

      const fetchPromises: Promise<void>[] = [];

      if (hasMenteeRole && !('menteeProgress' in cached)) {
        fetchPromises.push(
          API.getMenteeProgress({ orgId, grpId: cardId })
            .then((res: any) => {
              const data = res?.data?.data || res?.data;
              setCardMenteeProgress((prev) => ({ ...prev, [cardId]: data }));
              cardDataCacheRef.current[cardId] = {
                ...cardDataCacheRef.current[cardId],
                menteeProgress: data ?? null
              };
            })
            .catch(() => {
              setCardMenteeProgress((prev) => ({ ...prev, [cardId]: null }));
              cardDataCacheRef.current[cardId] = {
                ...cardDataCacheRef.current[cardId],
                menteeProgress: null
              };
            })
        );
      }

      if (!cached.menteeList) {
        fetchPromises.push(
          API.getMenteeListForMentor(orgId, location, cardId)
            .then((res: any) => {
              const data = res?.data?.data || res?.data || [];
              const normalized = normalizeDrawerMenteeList(data);
              cardDataCacheRef.current[cardId] = {
                ...cardDataCacheRef.current[cardId],
                menteeList: normalized
              };
            })
            .catch(() => {
              /* ignore */
            })
        );
      }

      await Promise.all(fetchPromises);

      const cardModel = resolveProgramModelFromDetailsPayload(detailPayload, card.model);
      if (
        hasMenteeRole &&
        programModelIncludesMasterclass(cardModel) &&
        orgId &&
        location &&
        (currentUserId || communicationUserId)
      ) {
        try {
          const res = await API.getMenteesPeersHomePage(orgId, location, cardId);
          const payload = res?.data?.data || res?.data;
          const mcq = pickSelfMcqScoreFromPeersPayload(payload, currentUserId, communicationUserId);
          if (mcq !== undefined) {
            setCardMenteeMcqScores((prev) => ({ ...prev, [cardId]: mcq }));
          }
        } catch {
          /* ignore */
        }
      }

      if (shouldFetchTasksForModel(cardModel)) {
        try {
          const res = await API.getAllTasks({ grpId: cardId });
          const payload = res?.data?.data || res?.data;
          const summary = computeTaskSummaryFromPayload(payload);
          if (summary) {
            setCardTaskSummaries((prev) => ({ ...prev, [cardId]: summary }));
          }
        } catch {
          /* ignore */
        }
      }

      setCardReady((prev) => ({ ...prev, [cardId]: true }));
    },
    [orgId, location, currentUserId, communicationUserId]
  );

  const prefetchAllCards = useCallback(
    async (cards: GroupedProgram[]) => {
      if (!cards.length || !orgId || !location) {
        setDashboardReady(true);
        return;
      }
      setDashboardReady(false);

      const progressBatch: Record<string, any> = {};
      const summaryBatch: Record<string, TaskSummary> = {};
      const mcqScoreBatch: Record<string, number | null> = {};
      const readyBatch: Record<string, boolean> = {};

      await Promise.all(
        cards.map(async (card) => {
          const cardId = card.id;
          if (!cardId) return;

          const cached = cardDataCacheRef.current[cardId] || {};
          const hasMenteeRole = card.roles.includes('mentee');

          let detailPayload = cached.details;
          if (!detailPayload) {
            try {
              const res = await API.getGroupDetails(cardId);
              detailPayload = res?.data?.data || res?.data;
              cardDataCacheRef.current[cardId] = {
                ...cardDataCacheRef.current[cardId],
                details: detailPayload
              };
            } catch {
              /* ignore */
            }
          }

          const fetches: Promise<void>[] = [];

          if (hasMenteeRole && !('menteeProgress' in cached)) {
            fetches.push(
              API.getMenteeProgress({ orgId, grpId: cardId })
                .then((res: any) => {
                  const data = res?.data?.data || res?.data;
                  progressBatch[cardId] = data;
                  cardDataCacheRef.current[cardId] = {
                    ...cardDataCacheRef.current[cardId],
                    menteeProgress: data ?? null
                  };
                })
                .catch(() => {
                  progressBatch[cardId] = null;
                })
            );
          }

          if (!cached.menteeList) {
            fetches.push(
              API.getMenteeListForMentor(orgId, location, cardId)
                .then((res: any) => {
                  const data = res?.data?.data || res?.data || [];
                  cardDataCacheRef.current[cardId] = {
                    ...cardDataCacheRef.current[cardId],
                    menteeList: normalizeDrawerMenteeList(data)
                  };
                })
                .catch(() => {
                  /* ignore */
                })
            );
          }

          await Promise.all(fetches);

          const cardModel = resolveProgramModelFromDetailsPayload(detailPayload, card.model);
          if (hasMenteeRole && programModelIncludesMasterclass(cardModel) && (currentUserId || communicationUserId)) {
            try {
              const res = await API.getMenteesPeersHomePage(orgId, location, cardId);
              const payload = res?.data?.data || res?.data;
              const mcq = pickSelfMcqScoreFromPeersPayload(payload, currentUserId, communicationUserId);
              if (mcq !== undefined) {
                mcqScoreBatch[cardId] = mcq;
              }
            } catch {
              /* ignore */
            }
          }

          if (shouldFetchTasksForModel(cardModel)) {
            try {
              const res = await API.getAllTasks({ grpId: cardId });
              const payload = res?.data?.data || res?.data;
              const summary = computeTaskSummaryFromPayload(payload);
              if (summary) summaryBatch[cardId] = summary;
            } catch {
              /* ignore */
            }
          }

          readyBatch[cardId] = true;
        })
      );

      // Fetch mentor stats in the same batch
      const mentorPrograms = cards.filter((c) => c.roles.includes('mentor'));
      const mentorStatsBatch: Record<
        string,
        { messagesSent: number; menteesEngaged: number; meetingsScheduled: number; meetingsCompleted: number }
      > = {};

      if (mentorPrograms.length) {
        const mentorEntries = await Promise.all(
          mentorPrograms.map(async (card) => {
            try {
              const [menteesResponse, meetingsResponse] = await Promise.all([
                API.getMyMentees({ orgId, groupId: card.id }),
                API.getMenteeListForMentor(orgId, location, card.id)
              ]);
              const menteesData = menteesResponse?.data?.data || menteesResponse?.data || [];
              const menteesList = Array.isArray(menteesData) ? menteesData : [];
              const messagesSent = menteesList.reduce((sum: number, item: any) => sum + (item?.messagesShared ?? 0), 0);
              const menteesEngaged = menteesList.length;

              const meetingsData = meetingsResponse?.data?.data || meetingsResponse?.data || [];
              const meetingsList = Array.isArray(meetingsData) ? meetingsData : [];
              const hasNestedMeetings = meetingsList.some(
                (m: any) =>
                  Array.isArray(m?.matchedObjectives) &&
                  m.matchedObjectives.some(
                    (o: any) => Array.isArray(o?.mentors) && o.mentors.some((r: any) => Array.isArray(r?.meetings))
                  )
              );
              let meetingsCompleted = 0;
              let meetingsScheduled = 0;
              if (hasNestedMeetings) {
                const now = Date.now();
                meetingsList.forEach((mentee: any) => {
                  (Array.isArray(mentee?.matchedObjectives) ? mentee.matchedObjectives : []).forEach((obj: any) => {
                    (Array.isArray(obj?.mentors) ? obj.mentors : []).forEach((mentor: any) => {
                      (Array.isArray(mentor?.meetings) ? mentor.meetings : []).forEach((m: any) => {
                        if (m?.cancelled) return;
                        const d = m?.callDetails || {};
                        const date = d?.callDate;
                        const time = d?.callTime;
                        if (date) {
                          const dateTimeString = time ? `${date}T${time}:00` : `${date}T00:00:00`;
                          const parsed = new Date(dateTimeString);
                          if (!Number.isNaN(parsed.getTime())) {
                            if (parsed.getTime() <= now) meetingsCompleted++;
                            else meetingsScheduled++;
                          }
                        }
                      });
                    });
                  });
                });
              } else {
                meetingsScheduled = meetingsList.reduce(
                  (sum: number, item: any) => sum + (item?.sessions ?? item?.upcomingMeetings ?? 0),
                  0
                );
              }
              return [card.id, { messagesSent, menteesEngaged, meetingsScheduled, meetingsCompleted }] as const;
            } catch {
              return [
                card.id,
                { messagesSent: 0, menteesEngaged: 0, meetingsScheduled: 0, meetingsCompleted: 0 }
              ] as const;
            }
          })
        );
        mentorEntries.forEach(([id, stats]) => {
          mentorStatsBatch[id] = stats;
        });
      }

      // Single batch state update -- all cards render at once
      setCardMenteeProgress((prev) => ({ ...prev, ...progressBatch }));
      setCardTaskSummaries((prev) => ({ ...prev, ...summaryBatch }));
      setCardMenteeMcqScores((prev) => ({ ...prev, ...mcqScoreBatch }));
      setMentorCardStats((prev) => ({ ...prev, ...mentorStatsBatch }));
      setCardReady((prev) => ({ ...prev, ...readyBatch }));
      setDashboardReady(true);
    },
    [orgId, location, currentUserId, communicationUserId]
  );

  const loadMatchesForRole = useCallback(
    async (grpId: string, model: string, objectives: any[], roleKey: 'mentor' | 'mentee') => {
      if (!grpId || !orgId) {
        setDrawerLoading(false);
        return;
      }
      setDrawerLoading(true);
      const entries = Array.isArray(objectives) ? objectives : [];
      const isGuided = model?.toLowerCase().includes('masterclass&mentorship');

      if (roleKey === 'mentee') {
        try {
          const res = await API.getMyMentors({ orgId, groupId: grpId });
          const data = res?.data?.data || res?.data || [];
          const list = Array.isArray(data) ? data : [];
          const map: Record<string, any[]> = {};

          list.forEach((mentor: any) => {
            const objectivesList =
              mentor?.matchedObjectives ||
              mentor?.extended?.learningObjectives ||
              mentor?.learningObjectives ||
              mentor?.objectives ||
              [];

            if (Array.isArray(objectivesList) && objectivesList.length) {
              objectivesList.forEach((objective: any) => {
                const key = objective?.id || objective?.objectiveId || objective?.objective || objective?.name;
                if (!key) return;
                const keyStr = String(key);
                if (!map[keyStr]) map[keyStr] = [];
                map[keyStr].push(mentor);
              });
              return;
            }

            const fallbackKey = mentor?.objectiveId || mentor?.objId || mentor?.objective || mentor?.objectiveName;
            if (fallbackKey) {
              const keyStr = String(fallbackKey);
              if (!map[keyStr]) map[keyStr] = [];
              map[keyStr].push(mentor);
            }
          });

          setObjectiveMatchesMap(map);
        } catch (err) {
          console.error('Failed to load mentee mentors', err);
          setObjectiveMatchesMap({});
        } finally {
          setDrawerLoading(false);
        }
        return;
      }

      if (isGuided) {
        try {
          const res = await API.getObjectiveMentors({ orgId, grpId, objId: roleKey });
          const data = res?.data?.data || res?.data || [];
          const map: Record<string, any[]> = {};
          (Array.isArray(data) ? data : []).forEach((match: any) => {
            const objectivesList =
              match?.extended?.learningObjectives || match?.matchedObjectives || match?.objectives || [];
            (Array.isArray(objectivesList) ? objectivesList : []).forEach((objective: any) => {
              const key = objective?.id || objective?.objectiveId || objective?.objective || objective?.name || roleKey;
              if (!key) return;
              if (!map[String(key)]) map[String(key)] = [];
              map[String(key)].push(match);
            });
          });
          setObjectiveMatchesMap(map);
        } catch (err) {
          console.error('Failed to load guided matches', roleKey, err);
        } finally {
          setDrawerLoading(false);
        }
        return;
      }

      if (roleKey === 'mentor') {
        try {
          const res = await API.getAllObjectiveMentors({ orgId, grpId });
          const data = res?.data?.data || res?.data || [];
          const map: Record<string, any[]> = {};
          (Array.isArray(data) ? data : []).forEach((match: any) => {
            const objectivesList =
              match?.matchedObjectives || match?.extended?.learningObjectives || match?.objectives || [];
            (Array.isArray(objectivesList) ? objectivesList : []).forEach((objective: any) => {
              const objId = objective?.id || objective?.objectiveId || objective?.objective || objective?.name;
              if (!objId) return;
              const key = String(objId);
              if (!map[key]) map[key] = [];
              map[key].push(match);
            });
          });
          setObjectiveMatchesMap(map);
        } catch (err) {
          console.error('Failed to load mentor matches', err);
        } finally {
          setDrawerLoading(false);
        }
        return;
      }

      await Promise.all(
        entries.map(async (obj) => {
          const objId = obj?.id || obj?.objectiveId || obj?.objId || obj?.name;
          if (!objId) return;
          try {
            const res = await API.getObjectiveMentors({ orgId, grpId, objId });
            const data = res?.data?.data || res?.data || [];
            const matchesArray = Array.isArray(data) ? data : [];
            setObjectiveMatchesMap((prev) => {
              const updated = { ...prev };
              const addToMap = (key: string, match: any) => {
                if (!updated[key]) updated[key] = [];
                if (!updated[key].some((existing) => existing?.id === match?.id)) {
                  updated[key].push(match);
                }
              };
              matchesArray.forEach((match) => {
                const objectiveEntries = match?.matchedObjectives || match?.extended?.learningObjectives || [];
                (Array.isArray(objectiveEntries) ? objectiveEntries : []).forEach((objective: any) => {
                  const key = String(
                    objective?.id || objective?.objectiveId || objective?.object || objective?.name || objId
                  );
                  if (key) addToMap(key, match);
                });
                if (!match?.matchedObjectives?.length) {
                  addToMap(String(objId), match);
                }
              });
              return updated;
            });
          } catch (err) {
            console.error('Failed to load objective matches', objId, err);
          }
        })
      );
      setDrawerLoading(false);
    },
    [orgId]
  );

  useEffect(() => {
    prefetchAllCards(grouped);
  }, [grouped, prefetchAllCards]);

  const filterNotExistingObjectives = () => {
    const userLoIds = new Set((userLearningObjectives || []).map((lo: any) => lo.id));
    const programObjectives = programDetails?.learningObjectives?.objectives || [];
    const notInUserLo = programObjectives.filter((lo: any) => !userLoIds.has(lo.id));
    setSelectLearningObjectivesList(notInUserLo);
  };

  const getFilteredObjectives = async () => {
    const programObjs = programDetails?.learningObjectives?.objectives || [];

    if (!programObjs.length) {
      setObjectivesUserCanAdd([]);
      setSelectedObjectiveId('');
      return;
    }

    try {
      const userObjectivesRes = await API.UserLearningObjectives({ orgId });
      const fetchedUserObjectives = userObjectivesRes?.data?.data || [];

      const existingKeys = new Set(
        fetchedUserObjectives.map((obj: any) => getObjectiveIdentifier(obj)).filter(Boolean)
      );

      const filtered = programObjs.filter((obj: any) => {
        const key = getObjectiveIdentifier(obj) || obj?.objective || obj?.name;
        return key ? !existingKeys.has(key) : false;
      });

      setObjectivesUserCanAdd(filtered);
      setSelectedObjectiveId((prev) => {
        if (!prev) return '';
        const isStillAvailable = filtered.some((obj: any) => {
          const key = getObjectiveIdentifier(obj) || obj?.objective || obj?.name;
          return key === prev;
        });
        return isStillAvailable ? prev : '';
      });
    } catch (error) {
      console.error('Failed to fetch user objectives', error);
      setObjectivesUserCanAdd([]);
      setSelectedObjectiveId('');
    }
  };

  const handleAddObjective = async () => {
    if (!selectedObjectiveId) return;
    setSavingObjective(true);
    try {
      const userObjectivesRes = await API.UserLearningObjectives({ orgId });
      const latestUserObjectives = userObjectivesRes?.data?.data || [];

      const existingKeys = new Set(latestUserObjectives.map((obj: any) => getObjectiveIdentifier(obj)).filter(Boolean));

      const selectedOption =
        objectivesUserCanAdd.find(
          (obj: any) => (getObjectiveIdentifier(obj) || obj?.objective || obj?.name) === selectedObjectiveId
        ) || objectivesUserCanAdd.find((obj: any) => (obj?.objective || obj?.name) === selectedObjectiveId);

      const selectedKey = getObjectiveIdentifier(selectedOption) || selectedObjectiveId;

      if (selectedKey && !existingKeys.has(selectedKey)) {
        const newEntry = selectedOption || { objective: selectedObjectiveId };
        const updatedLearningObjectives = [...latestUserObjectives, newEntry];
        await API.saveLearningObjectives({ learningObjectives: updatedLearningObjectives });
        dispatch(userActions.updateObjectives(updatedLearningObjectives));
      }

      await getFilteredObjectives();
      setObjectiveModalOpen(false);
      setSelectedObjectiveId('');
      if (drawerOpen && drawerProgram) {
        refetchDrawerData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSavingObjective(false);
    }
  };

  useEffect(() => {
    getFilteredObjectives();
  }, [programDetails]);

  useEffect(() => {
    const programObjectives = programDetails?.learningObjectives?.objectives || [];
    const userObjectiveIds = new Set(
      (userLearningObjectives || [])
        .map((lo: any) => getObjectiveIdentifier(lo) || lo?.id || lo?._id || lo?.objective || lo?.name)
        .filter(Boolean)
        .map((id: any) => String(id))
    );
    const matchedObjectiveIds = new Set<string>();

    if (drawerRole === 'mentee') {
      Object.entries(objectiveMatchesMap || {}).forEach(([key, matches]) => {
        if ((matches || []).length) {
          matchedObjectiveIds.add(String(key));
        }
      });
    }

    (menteeList || []).forEach((mentee: any) => {
      const matched = Array.isArray(mentee?.matchedObjectives) ? mentee.matchedObjectives : [];
      matched.forEach((obj: any) => {
        const key = getObjectiveIdentifier(obj) || obj?.id || obj?._id || obj?.objective || obj?.name;
        if (key) matchedObjectiveIds.add(String(key));
      });
    });

    const collected = (Array.isArray(programObjectives) ? programObjectives : []).filter((obj: any) => {
      const key = getObjectiveIdentifier(obj) || obj?.id || obj?._id || obj?.objective || obj?.name;
      if (!key) return false;
      const id = String(key);
      if (userObjectiveIds.has(id)) return true;
      return matchedObjectiveIds.has(id);
    });

    if (!collected.length) {
      setCreatedLearningObjectivesList([]);
      setExpandedObjectives({});
      return;
    }

    setCreatedLearningObjectivesList(collected);
    const firstId =
      getObjectiveIdentifier(collected[0]) || collected[0]?.id || collected[0]?._id || collected[0]?.objective;
    if (!firstId) return;
    setExpandedObjectives((prev) => (prev[firstId] ? prev : { [firstId]: true }));
  }, [programDetails, userLearningObjectives, menteeList, drawerRole, objectiveMatchesMap]);

  const drawerObjectiveMenteesMap = useMemo(() => {
    const map: Record<string, any[]> = {};
    (menteeList || []).forEach((mentee) => {
      const matched = mentee?.matchedObjectives || [];
      matched.forEach((obj: any, idx: number) => {
        const key =
          getObjectiveIdentifier(obj) ||
          obj?.objective ||
          obj?.name ||
          obj?.objectiveId ||
          obj?.id ||
          obj?._id ||
          `${obj?.objective || 'objective'}_${idx}`;
        if (!key) return;
        if (!map[key]) map[key] = [];
        map[key].push({
          ...mentee,
          objective: obj?.objective || obj?.name
        });
      });
    });
    return map;
  }, [menteeList]);

  const displayLearningObjectives = useMemo(() => {
    if (drawerRole === 'mentee') {
      return Array.isArray(drawerUserLearningObjectives) ? drawerUserLearningObjectives : [];
    }

    // Mentor: derive expertise accordions from getMenteeListForMentor response (menteeList)
    const list = Array.isArray(menteeList) ? menteeList : [];
    const seenIds = new Set<string>();
    const objectivesFromMentees: any[] = [];
    list.forEach((mentee: any) => {
      const matched = Array.isArray(mentee?.matchedObjectives) ? mentee.matchedObjectives : [];
      matched.forEach((mo: any) => {
        const id = mo?.id || mo?._id || mo?.objectiveId;
        const key = id ? String(id) : mo?.objective || mo?.name || '';
        if (!key) return;
        if (seenIds.has(key)) return;
        seenIds.add(key);
        objectivesFromMentees.push({
          id: id || key,
          _id: mo?._id || id,
          objective: mo?.objective ?? mo?.name ?? key,
          name: mo?.objective ?? mo?.name ?? key,
          status: mo?.status,
          description: mo?.description,
          mentors: mo?.mentors,
          notes: mo?.notes
        });
      });
    });
    if (objectivesFromMentees.length > 0) return objectivesFromMentees;

    const programObjectives = programDetails?.learningObjectives?.objectives || [];
    const programIds = new Set(
      (Array.isArray(programObjectives) ? programObjectives : [])
        .map((obj: any) => getObjectiveIdentifier(obj) || obj?.id || obj?._id || obj?.objective || obj?.name)
        .filter(Boolean)
        .map((id: any) => String(id))
    );
    if (!programIds.size) return [];
    return (createdLearningObjectivesList || []).filter((obj: any) => {
      const key = getObjectiveIdentifier(obj) || obj?.id || obj?._id || obj?.objective || obj?.name;
      return key ? programIds.has(String(key)) : false;
    });
  }, [createdLearningObjectivesList, programDetails, drawerRole, drawerUserLearningObjectives, menteeList]);

  // "40 min", "1 hr 20 min": the same words as the header bar's time remaining.
  const formatAdviceTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    if (hours === 0) return `${mins} min`;
    return mins === 0 ? `${hours} hr` : `${hours} hr ${mins} min`;
  };

  const mentorStats = useMemo(() => {
    const list = menteeList || [];
    const menteeIds = new Set(list.map((mentee) => mentee?.id || mentee?.menteeId || mentee?.userId).filter(Boolean));

    const messagesSent = list.reduce(
      (sum, mentee) => sum + (mentee?.messagesShared ?? mentee?.messageCount ?? mentee?.messagesCount ?? 0),
      0
    );

    const hasNestedMeetings = list.some(
      (m: any) =>
        Array.isArray(m?.matchedObjectives) &&
        m.matchedObjectives.some(
          (o: any) => Array.isArray(o?.mentors) && o.mentors.some((r: any) => Array.isArray(r?.meetings))
        )
    );
    let meetingsCompleted: number;
    let futureMeetings: number;
    if (hasNestedMeetings) {
      const now = Date.now();
      let past = 0;
      let future = 0;
      list.forEach((mentee: any) => {
        (Array.isArray(mentee?.matchedObjectives) ? mentee.matchedObjectives : []).forEach((obj: any) => {
          (Array.isArray(obj?.mentors) ? obj.mentors : []).forEach((mentor: any) => {
            (Array.isArray(mentor?.meetings) ? mentor.meetings : []).forEach((m: any) => {
              if (m?.cancelled) return;
              const d = m?.callDetails || {};
              const date = d?.callDate;
              const time = d?.callTime;
              if (date) {
                const dateTimeString = time ? `${date}T${time}:00` : `${date}T00:00:00`;
                const parsed = new Date(dateTimeString);
                if (!Number.isNaN(parsed.getTime())) {
                  if (parsed.getTime() <= now) past++;
                  else future++;
                }
              }
            });
          });
        });
      });
      meetingsCompleted = past;
      futureMeetings = future;
    } else {
      meetingsCompleted = list.reduce(
        (sum, mentee) => sum + (mentee?.meetingsCompleted ?? mentee?.completedMeetings ?? 0),
        0
      );
      futureMeetings = list.reduce((sum, mentee) => sum + (mentee?.upcomingMeetings ?? mentee?.futureMeetings ?? 0), 0);
    }

    const adviceMinutes = (menteeList || []).reduce(
      (sum, mentee) => sum + (mentee?.adviceMinutes ?? mentee?.adviceDuration ?? 0),
      0
    );

    const xpPoints = (menteeList || []).reduce(
      (sum, mentee) => sum + (mentee?.xpPoints ?? mentee?.xp ?? mentee?.xpEarned ?? 0),
      0
    );

    return {
      menteesEngaged: menteeIds.size,
      messagesSent,
      meetingsCompleted,
      futureMeetings,
      adviceMinutes,
      xpPoints
    };
  }, [menteeList]);

  const mentorshipStatRows = useMemo(
    () => [
      [
        { label: `${menteesWord} Engaged`, value: mentorStats.menteesEngaged },
        {
          label: 'Messages Shared',
          value: mentorCardStats[drawerProgram?.id ?? '']?.messagesSent ?? mentorStats.messagesSent
        }
      ],
      [
        { label: 'Meetings Completed', value: mentorStats.meetingsCompleted },
        { label: 'Future Meetings', value: mentorStats.futureMeetings }
      ],
      [
        { label: 'Advice Given', value: formatAdviceTime(mentorStats.adviceMinutes) },
        { label: 'XP Points', value: mentorStats.xpPoints }
      ]
    ],
    [mentorStats, mentorCardStats, drawerProgram?.id, menteesWord]
  );

  const parseNumber = (value: any) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const parseCallDateTime = (call: any) => {
    const date = call?.callDate;
    const time = call?.callTime;
    if (!date) return null;
    const dateTimeString = time ? `${date}T${time}:00` : `${date}T00:00:00`;
    const parsed = new Date(dateTimeString);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isCompletedMeeting = (call: any) => {
    const status = (call?.callStatus || '').toString().toLowerCase();
    if (status.includes('cancel')) return false;
    if (status.includes('complete') || status.includes('done') || status.includes('finished')) return true;
    const dateTime = parseCallDateTime(call);
    if (dateTime) {
      return dateTime.getTime() <= Date.now();
    }
    return false;
  };

  const isFutureMeeting = (call: any) => {
    const status = (call?.callStatus || '').toString().toLowerCase();
    if (status.includes('cancel')) return false;
    const dateTime = parseCallDateTime(call);
    if (dateTime) {
      return dateTime.getTime() > Date.now();
    }
    return status.includes('initiated') || status.includes('scheduled');
  };

  useEffect(() => {
    if (!drawerOpen) {
      setMenteeProgressStats(DEFAULT_MENTEE_STATS);
      setMenteeInsightsStats({});
    }
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerProgram || drawerRole !== 'mentee' || !drawerOpen) return undefined;

    let canceled = false;
    const fetchStats = async () => {
      try {
        const [progressResponse, mentorsResponse, callsResponse] = await Promise.all([
          API.getMenteeProgress({ orgId, grpId: drawerProgram.id }),
          API.getMyMentors({ orgId, groupId: drawerProgram.id }),
          API.getScheduleCall(currentUserId, userDetails.communicationUserId, orgId, location, 1, 200)
        ]);
        if (!canceled) {
          const progressData = progressResponse?.data?.data || progressResponse?.data || {};
          const mentorsData = mentorsResponse?.data?.data || mentorsResponse?.data || [];
          const mentorsList = Array.isArray(mentorsData) ? mentorsData : [];
          const mentorIds = new Set(
            mentorsList
              .map((mentor) => mentor?.userId || mentor?.mid || mentor?.id || mentor?.mentorId)
              .filter(Boolean)
              .map((id) => String(id))
          );
          const callsData = callsResponse?.data?.data || callsResponse?.data || [];
          const callsList = Array.isArray(callsData) ? callsData : [];
          const filteredCalls = callsList.filter((call) => {
            const bookedWithId = call?.callBookedWith?.id || call?.callBookedWith?.userId || call?.callBookedWith;
            const bookedById = call?.callBookedBy?.id || call?.callBookedBy?.userId || call?.callBookedBy;
            const participantIds = Array.isArray(call?.participantsUser)
              ? call.participantsUser
                  .map((p: any) => p?.mid || p?.userId || p?.id)
                  .filter(Boolean)
                  .map((id: any) => String(id))
              : [];
            return (
              (bookedWithId && mentorIds.has(String(bookedWithId))) ||
              (bookedById && mentorIds.has(String(bookedById))) ||
              participantIds.some((id: string) => mentorIds.has(id))
            );
          });

          const meetingsCompleted = filteredCalls.reduce((sum, call) => sum + (isCompletedMeeting(call) ? 1 : 0), 0);
          const futureMeetings = filteredCalls.reduce((sum, call) => sum + (isFutureMeeting(call) ? 1 : 0), 0);
          setMenteeProgressStats({
            mentorsEngaged: mentorsList.length,
            messagesSent: parseNumber(
              progressData?.messagesSent ?? progressData?.messageCount ?? progressData?.messages ?? 0
            ),
            meetingsCompleted,
            futureMeetings
          });
        }
      } catch (error) {
        console.error('Failed to fetch mentee progress stats', error);
      }

      try {
        const insights = await API.getMenteeInsights(orgId, location, drawerProgram.id);
        if (!canceled) {
          const insightData = insights?.data?.data || insights?.data || {};
          setMenteeInsightsStats({ xpPoints: parseNumber(insightData?.xpPoints ?? insightData?.xp ?? 0) });
        }
      } catch (error) {
        console.error('Failed to fetch mentee insights', error);
      }
    };

    fetchStats();

    return () => {
      canceled = true;
    };
  }, [drawerProgram, drawerRole, drawerOpen, orgId, location]);

  const menteeProgramObjectives =
    typeof programDetails?.learningObjectives?.number === 'number'
      ? programDetails.learningObjectives.number
      : programDetails?.learningObjectives?.objectives?.length ?? 0;
  const menteeStatRows = useMemo(
    () => [
      [
        { label: 'Program Objectives', value: menteeProgramObjectives },
        { label: `${mentorsWord} Engaged`, value: menteeProgressStats.mentorsEngaged }
      ],
      [
        { label: 'Messages Shared', value: menteeProgressStats.messagesSent },
        { label: 'Meetings Completed', value: menteeProgressStats.meetingsCompleted }
      ],
      [
        { label: 'Future Meetings', value: menteeProgressStats.futureMeetings },
        { label: 'XP Points', value: menteeInsightsStats.xpPoints ?? 0 }
      ]
    ],
    [menteeInsightsStats.xpPoints, menteeProgressStats, menteeProgramObjectives, mentorsWord]
  );

  useEffect(() => {
    const objectives = programDetails?.learningObjectives?.objectives || [];
    if (!objectives.length) return;
    const firstId = objectives[0]?.id || objectives[0]?._id || objectives[0]?.objective;
    if (!firstId) return;
    filterNotExistingObjectives();
    setExpandedObjectives((prev) => (prev[firstId] ? prev : { [firstId]: true }));
  }, [programDetails, userLearningObjectives]);

  useEffect(() => {
    if (!drawerProgram) return;
    const objectiveDefs =
      programDetails?.learningObjectives?.objectives || programDetails?.learningObjectives?.data || [];
    const model =
      programDetails?.model ||
      drawerProgram?.model ||
      drawerProgram?.raw?.model ||
      drawerProgram?.raw?.groupDetails?.[0]?.model ||
      '';
    const viewRole = drawerRole === 'mentor' ? 'mentor' : 'mentee';
    setObjectiveMatchesMap({});
    loadMatchesForRole(drawerProgram.id, model, objectiveDefs, viewRole);
  }, [drawerProgram, drawerRole, programDetails, loadMatchesForRole]);

  const getDefaultRole = (roles: RoleKey[]) => {
    const sorted = [...roles].sort((a, b) => rolePriority.indexOf(a) - rolePriority.indexOf(b));
    return sorted[0] || 'mentee';
  };

  const syncReduxForRole = (card: GroupedProgram, role: RoleKey) => {
    // Prefer the card's own entries. Stub cards (from MemberLearningLifecycleDashboard)
    // arrive with entries:[] — fall back to groupRoleList so Redux is updated before
    // navigation fires, fixing "Next Lesson requires 2-3 clicks".
    const selectedEntry: IRoleObj =
      card.entries.find((e: any) => e.role === role) ||
      card.entries[0] ||
      (groups as IRoleObj[]).find((g) => g.id === card.id && g.role === role) ||
      (groups as IRoleObj[]).find((g) => g.id === card.id) ||
      // Last resort, and never a no-op. A learner who has just been enrolled is
      // not in groupRoleList yet, so every lookup above misses; this used to
      // return without touching Redux while the caller navigated anyway, and
      // the lesson page — which reads the program from Redux — opened with none
      // and rendered the demo walkthrough ("Sales Onboarding", Module 1 ticked)
      // to a real learner. The card already knows the program, and the lesson
      // page only needs id/name/role.
      ({ id: card.id, name: card.name, role, active: true } as IRoleObj);

    dispatch(updateSelectedId(selectedEntry));
    if (selectedEntry.role === 'mentor' || selectedEntry.role === 'consultant') {
      dispatch(fetchMentorTasks({ groupId: selectedEntry.id }));
    }
    dispatch(setSelectedValue(`${selectedEntry.id}_${selectedEntry.role}`));
    localStorage.setItem('lastseen', JSON.stringify(selectedEntry));
  };

  // Improve Performance CTA (L-11/L-14): sync Redux for the mentee role, then
  // route straight to the earliest outstanding item's lesson in retake mode.
  const handleImprovePerformance = async (card: GroupedProgram) => {
    syncReduxForRole(card, 'mentee');
    try {
      const res = await API.getElliottImproveQueue({ groupId: card.id });
      const item = (res.data?.items || [])[0];
      if (item) {
        navigate(`/app/home/mentee/level/${item.levelId}/task/${item.taskId}?improve=1`);
        return;
      }
    } catch {
      /* queue unavailable — fall back to the normal program entry */
    }
    handleSelectProgram(card.id, 'mentee');
  };

  const handleSelectProgram = (cardId: string, preferredRole?: RoleKey) => {
    // Prefer the fully-hydrated card from `grouped` (real entries → correct
    // Redux sync). Fall back to the drawer's program, which may be a stub
    // pushed by the Learning Lifecycle host before `grouped` hydrates — so
    // "Next Lesson" always navigates instead of silently no-op'ing.
    const card = grouped.find((g) => g.id === cardId) || (drawerProgram?.id === cardId ? drawerProgram : undefined);
    if (!card) return;

    const selectedRole = preferredRole || roleSelections[card.id] || getDefaultRole(card.roles);
    syncReduxForRole(card, selectedRole);
    navigate(`/app/home/${selectedRole}`);
  };

  /**
   * The drawer's masterclass CTA ("Next Lesson"). It used to call
   * handleManagePortfolio, which routes to /app/home/<role> — the program
   * landing, not a lesson. That route is gated on the profile's `mentees`
   * array, so a click made before the profile lands in redux redirected
   * straight back out (the "Next Lesson requires 2-3 clicks" note above is the
   * same race seen from the other side). Deep-linking to the lesson route
   * skips both problems: it is the exact path the card's own Continue uses,
   * and it is registered outside the role gate.
   *
   * Falls back to the old behaviour when we have no resume lesson (every
   * lesson complete, or a stub that predates this data).
   */
  const handleNextLesson = async (program?: GroupedProgram) => {
    const target = program || drawerProgram;
    if (!target) return;
    if (target.raw?.locked === true) return;
    const rl = target.raw?.resumeLesson;
    if (!rl?.moduleId || !rl?.taskId) {
      await handleManagePortfolio(target);
      return;
    }
    syncReduxForRole(target, 'mentee');
    try {
      await API.updateLastseen({ id: target.id, role: 'mentee', name: target.name });
    } catch (error) {
      /* last-seen is a convenience, never block the lesson on it */
    }
    navigate(`/app/home/mentee/level/${rl.moduleId}/task/${rl.taskId}`);
  };

  const handleManagePortfolio = async (program?: GroupedProgram) => {
    const targetProgram = program || drawerProgram;
    if (!targetProgram) return;
    const roleToUse = program ? roleSelections[targetProgram.id] || getDefaultRole(targetProgram.roles) : drawerRole;
    try {
      await API.updateLastseen({
        id: targetProgram.id,
        role: roleToUse,
        name: targetProgram.name
      });
    } catch (error) {
      console.error('Failed to update last seen', error);
    }
    handleSelectProgram(targetProgram.id, roleToUse);
  };

  const getCompletionPercent = (mentee: any) => {
    const fallbackValues = [
      mentee?.progressPercentage,
      mentee?.progressPercent,
      mentee?.completionPercent,
      mentee?.progress?.percentage,
      mentee?.progress?.completionPercent,
      mentee?.progress?.progressPercentage,
      mentee?.progress?.progress,
      mentee?.percent
    ];
    for (const value of fallbackValues) {
      const normalized = toSafeNumber(value);
      if (normalized !== undefined) {
        return Math.min(100, Math.max(0, normalized));
      }
    }
    return 0;
  };

  const renderDrawerSkeletonLines = (lines = 4) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      {Array.from({ length: lines }).map((_, idx) => (
        <Skeleton key={`drawer-skel-${idx}`} variant="rectangular" height={20} width="100%" />
      ))}
    </Box>
  );

  const handleStartConversation = (mentee: any) => {
    const communicationId =
      mentee?.communicationId || mentee?.communication_id || mentee?.commId || mentee?.communicationID;
    if (!communicationId) {
      return;
    }
    const listedInCommunity = communityMembers?.find((item) => item?.id?.communicationId === communicationId);
    const isInCommunity = Boolean(listedInCommunity);
    navigateToChat(communicationId, isInCommunity, personalThreadsArr, navigate, dispatch);
  };

  const handleOpenMenu = (event: MouseEvent<HTMLElement>, mentee: any) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuMentee(mentee);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuMentee(null);
  };

  const handleViewProfile = async (mentee: any) => {
    handleCloseMenu();
    try {
      const userId = mentee?.id || mentee?.menteeId || mentee?.userId;
      if (!userId) {
        setProfileUser(mentee);
        setProfileModalOpen(true);
        return;
      }
      const response = await API.getUserDetailsById(userId);
      setProfileUser(response?.data?.data || mentee);
    } catch (error) {
      console.error('Failed to fetch mentee profile', error);
      setProfileUser(mentee);
    } finally {
      setProfileModalOpen(true);
    }
  };

  const handleMeetingHistory = (mentee: any) => {
    handleCloseMenu();
    setMeetingMentee(mentee);
    setMeetingModalOpen(true);
  };

  const handleCloseMeetingModal = () => {
    setMeetingModalOpen(false);
    setMeetingMentee(null);
  };

  const handleViewMenteeProgress = async () => {
    handleCloseMenu();
    if (isGuidedMasterclassCard && menuMentee && drawerProgram) {
      try {
        const communicationId =
          menuMentee?.communicationId ||
          menuMentee?.communication_id ||
          menuMentee?.commId ||
          menuMentee?.communicationID;
        const acsUser = getAcsUserByCommunicationId(communityMembers, communicationId);
        const menteeUserId = _.get(acsUser, 'id.coId') || menuMentee?.id || menuMentee?.menteeId || menuMentee?.userId;
        if (!menteeUserId) {
          throw new Error('Mentee ID not available');
        }
        const grpId = drawerProgram.id;
        dispatch(taskActions.setUserId({ userId: menteeUserId }));
        dispatch(taskActions.setGroupId({ groupId: grpId }));
        const response = await API.getAllTasks({ grpId, userId: menteeUserId });
        const data = _.get(response, 'data.data', []);
        const levels = _.get(_.head(data), 'levels');
        const levelId = _.get(_.head(levels), '_id');
        const tasks = _.get(_.head(levels), 'tasks');
        const taskId = _.get(_.head(tasks), '_id');
        if (levelId && taskId) {
          const encodedName = encodeURIComponent(getDisplayName(menuMentee) || menuMentee?.displayName || '');
          navigate(`/app/home/mentee/level/${levelId}/task/${taskId}?userName=${encodedName}`);
        }
      } catch (error) {
        toast.error(_.get(error, 'response.data.message', `Failed to open ${menteeWordLower} lessons`));
      }
      return;
    }
    handleManagePortfolio();
  };

  const handleOpenDrawer = (card: GroupedProgram, preferredRole?: string) => {
    setDrawerProgram(card);
    drawerCardIdRef.current = card.id;
    setExpandedObjectives({});
    const defaultRole = (preferredRole as RoleKey) || roleSelections[card.id] || getDefaultRole(card.roles);
    setDrawerRole(defaultRole);
    setLearningGrpId(card.id);
    setDrawerOpen(true);
    setDrawerLoading(true);
    setObjectiveMatchesMap({});
    setProgramDetails(null);

    syncReduxForRole(card, defaultRole);

    const cached = cardDataCacheRef.current[card.id] || {};
    const detail = cached.details || card.details || card.raw?.groupDetails?.[0] || card.raw?.details || {};
    const progressData = cached.menteeProgress || card.raw?.progress || card.raw?.goalProgress || null;

    setProgramDetails(detail || null);
    if (progressData) {
      setCardMenteeProgress((prev) => ({ ...prev, [card.id]: progressData }));
    }

    const menteeList = cached.menteeList || card.raw?.peers?.menteePeers?.menteeDetails || [];
    const normalizedList = normalizeDrawerMenteeList(menteeList);
    applyDrawerMenteeData(normalizedList);
    const viewRole = defaultRole === 'mentor' ? 'mentor' : 'mentee';
    const objectiveDefs = detail?.learningObjectives?.objectives || detail?.learningObjectives?.data || [];
    loadMatchesForRole(card.id, detail?.model || card.model || '', objectiveDefs, viewRole);

    // LL Member dashboard opens the drawer via a stub before the card cache is
    // populated, so `detail` can be {} with no objectives catalog — and nothing
    // downstream re-hydrates programDetails (the stub→real upgrade was removed).
    // Without the catalog the mentor expertise fallback and the mentee objective
    // matching both run against an empty list for the whole drawer session.
    if (!detail?.learningObjectives) {
      API.getGroupDetails(card.id)
        .then((res: any) => {
          const fetched = res?.data?.data || res?.data;
          if (!fetched) return;
          cardDataCacheRef.current[card.id] = { ...cardDataCacheRef.current[card.id], details: fetched };
          if (drawerCardIdRef.current !== card.id) return;
          setProgramDetails(fetched);
          const fetchedDefs = fetched?.learningObjectives?.objectives || fetched?.learningObjectives?.data || [];
          if (fetchedDefs.length) {
            loadMatchesForRole(card.id, fetched?.model || card.model || '', fetchedDefs, viewRole);
          }
        })
        .catch(() => {
          /* keep whatever detail resolved from the cache/card */
        });
    }
  };

  const fetchDrawerMentorMentees = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId || !location) return;
      try {
        const response = await API.getMenteeListForMentor(orgId, location, grpId);
        const data = response?.data?.data || response?.data || [];
        applyDrawerMenteeData(normalizeDrawerMenteeList(data));
      } catch (error) {
        console.error('Failed to fetch mentee list for mentor drawer', error);
      }
    },
    [orgId, location, applyDrawerMenteeData]
  );

  const fetchDrawerGuidedMasterclassMentees = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId) return;
      try {
        const response = await API.getMyMentees({ orgId, groupId: grpId });
        const data = response?.data?.data || response?.data || [];
        applyDrawerMenteeData(normalizeDrawerMenteeList(data));
      } catch (error) {
        console.error('Failed to fetch guided masterclass mentees', error);
      }
    },
    [orgId, applyDrawerMenteeData]
  );

  const drawerModelValue =
    programDetails?.model ||
    drawerProgram?.model ||
    drawerProgram?.raw?.model ||
    drawerProgram?.raw?.groupDetails?.[0]?.model ||
    '';
  const isGuidedMasterclassDrawer = useMemo(() => {
    const normalized = drawerModelValue?.toString().toLowerCase() || '';
    return normalized.includes('guided') || (normalized.includes('masterclass') && normalized.includes('mentorship'));
  }, [drawerModelValue]);

  const fetchDrawerGuidedMasterclassMentors = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId) return;
      try {
        const response = await API.getMyMentors({ orgId, groupId: grpId });
        const data = response?.data?.data || response?.data || [];
        applyDrawerMenteeData(normalizeDrawerMenteeList(data));
      } catch (error) {
        console.error('Failed to fetch guided masterclass mentors', error);
      }
    },
    [orgId, applyDrawerMenteeData]
  );

  useEffect(() => {
    if (!drawerOpen || !drawerProgram?.id) return;
    if (drawerRole === 'mentor') {
      if (isGuidedMasterclassDrawer) {
        fetchDrawerGuidedMasterclassMentees(drawerProgram.id);
      } else {
        fetchDrawerMentorMentees(drawerProgram.id);
      }
      return;
    }

    if (drawerRole === 'mentee' && isGuidedMasterclassDrawer) {
      fetchDrawerGuidedMasterclassMentors(drawerProgram.id);
    }
  }, [
    drawerOpen,
    drawerRole,
    drawerProgram?.id,
    isGuidedMasterclassDrawer,
    fetchDrawerMentorMentees,
    fetchDrawerGuidedMasterclassMentees,
    fetchDrawerGuidedMasterclassMentors
  ]);

  useEffect(() => {
    if (!drawerOpen || drawerRole !== 'mentee') return;
    fetchDrawerUserLearningObjectives();
  }, [drawerOpen, drawerRole, fetchDrawerUserLearningObjectives]);

  useEffect(() => {
    if (!drawerOpen || drawerRole !== 'mentee' || !learningGrpId || !drawerProgram) return;
    const drawerModel = getCardModel(drawerProgram) || '';
    const isMentorshipDrawer = drawerModel.toString().toLowerCase().includes('mentorship');
    if (!isMentorshipDrawer) return;

    fetchDrawerLearningObjectivesForMentee(learningGrpId);
  }, [drawerOpen, drawerRole, learningGrpId, drawerProgram, fetchDrawerLearningObjectivesForMentee]);

  useEffect(() => {
    if (!drawerOpen || !learningGrpId || !drawerProgram) return;
    const drawerModel = getCardModel(drawerProgram) || '';
    const isMasterclassDrawer = drawerModel.toString().toLowerCase().includes('masterclass');
    if (!isMasterclassDrawer) return;
    loadLeaderboardPeers(learningGrpId);
  }, [drawerOpen, learningGrpId, drawerProgram, loadLeaderboardPeers]);

  const refetchDrawerData = useCallback(async () => {
    if (!drawerProgram?.id || !drawerOpen) return;
    setDrawerLoading(true);
    const model =
      programDetails?.model ||
      drawerProgram?.model ||
      drawerProgram?.raw?.model ||
      drawerProgram?.raw?.groupDetails?.[0]?.model ||
      '';
    const objectiveDefs =
      programDetails?.learningObjectives?.objectives || programDetails?.learningObjectives?.data || [];
    const viewRole = drawerRole === 'mentor' ? 'mentor' : 'mentee';
    try {
      if (drawerRole === 'mentee') {
        await fetchDrawerUserLearningObjectives();
        if (learningGrpId) {
          const modelStr = (model || '').toString().toLowerCase();
          if (modelStr.includes('mentorship')) await fetchDrawerLearningObjectivesForMentee(learningGrpId);
        }
      } else {
        if (isGuidedMasterclassDrawer) await fetchDrawerGuidedMasterclassMentees(drawerProgram.id);
        else await fetchDrawerMentorMentees(drawerProgram.id);
      }
      await loadMatchesForRole(drawerProgram.id, model, objectiveDefs, viewRole);
    } catch (err) {
      console.error('Failed to refetch drawer data', err);
    } finally {
      setDrawerLoading(false);
    }
  }, [
    drawerOpen,
    drawerProgram,
    drawerRole,
    learningGrpId,
    programDetails,
    isGuidedMasterclassDrawer,
    fetchDrawerUserLearningObjectives,
    fetchDrawerLearningObjectivesForMentee,
    fetchDrawerMentorMentees,
    fetchDrawerGuidedMasterclassMentees,
    loadMatchesForRole
  ]);

  const renderRoleToggle = (card: GroupedProgram) => {
    const roles = card.roles.filter((r) => r !== 'unknown');
    if (!roles.length) return null;

    if (roles.length < 2) {
      return (
        <Box
          sx={{
            px: '8px',
            py: '5px',
            borderRadius: '6px',
            background: '#444444',
            color: '#FDFDFD',
            fontSize: 10,
            fontWeight: 700
          }}
        >
          {roleLabel(roles[0] || 'member')}
        </Box>
      );
    }

    const value = roleSelections[card.id] || getDefaultRole(roles);

    return (
      <ToggleButtonGroup
        size="small"
        value={value}
        exclusive
        onChange={(_, val: RoleKey | null) => {
          if (!val) return;
          setRoleSelections((prev) => ({ ...prev, [card.id]: val }));
          syncReduxForRole(card, val);
        }}
        sx={{
          background: '#2A2A2A',
          borderRadius: '6px',
          overflow: 'hidden',
          '& .MuiToggleButton-root': {
            color: '#B6B6B6',
            fontSize: 10,
            fontWeight: 400,
            textTransform: 'none',
            px: 1,
            '&.Mui-selected': {
              background: '#444444',
              color: '#FDFDFD',
              fontWeight: 700
            }
          }
        }}
      >
        {roles.map((role) => (
          <ToggleButton key={role} value={role}>
            {roleLabel(role)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  const getCardModel = (card: GroupedProgram) => {
    const cached = cardDataCacheRef.current[card.id]?.details;
    return cached?.model || cached?.goals?.model || cached?.goals?.programType || card.model;
  };

  const getProgramTag = (card: GroupedProgram, overrideModel?: string) => {
    const rawType: string = overrideModel || card.model || 'mentorship';

    const lower = rawType.toString().toLowerCase();

    if (lower.includes('masterclass') && lower.includes('mentorship')) {
      return {
        label: 'Guided Course',
        borderColor: '#F15A26',
        textColor: 'black'
      };
    }
    if (lower.includes('guided')) {
      return {
        label: 'Guided Course',
        borderColor: '#F15A26',
        textColor: 'black'
      };
    }
    if (lower.includes('masterclass')) {
      return {
        label: 'Course',
        borderColor: '#F0B80D',
        textColor: 'black'
      };
    }

    // default mentorship
    return {
      label: 'Mentorship Program',
      borderColor: '#8E44FF',
      textColor: 'white'
    };
  };

  const renderCard = (card: GroupedProgram, columnStatus: ProgramStatusKey) => {
    const selectedRole = roleSelections[card.id] || getDefaultRole(card.roles);
    const cardModel = getCardModel(card);
    const { label: tagLabel, borderColor, textColor } = getProgramTag(card, cardModel);
    const isMentorshipProgram = tagLabel === 'Mentorship Program';
    const isGuidedMasterclass = tagLabel === 'Guided Course';
    const isMasterclassProgram = tagLabel === 'Course';
    const cachedDetails = cardDataCacheRef.current[card.id]?.details ?? card.details;
    const programCreator = getProgramCreatorFromGroupOrProgramDetails(cachedDetails);
    const mcqFromRaw = resolveMcqScoreFromProgramRaw(card);
    const showMenteeMcqBadge = selectedRole === 'mentee' && (isMasterclassProgram || isGuidedMasterclass);
    // Null from the program's leaderboard: nothing in it assesses them (DA-5056 R5), so "-", not 0%.
    const menteeMcqNotRequired = showMenteeMcqBadge && cardMenteeMcqScores[card.id] === null;
    const menteeMcqPercent =
      showMenteeMcqBadge && !menteeMcqNotRequired
        ? Math.max(0, Math.min(100, Math.round(Number(cardMenteeMcqScores[card.id] ?? mcqFromRaw ?? 0))))
        : 0;
    const handleContinueClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isMentorshipProgram) {
        handleOpenDrawer(card);
        return;
      }
      handleManagePortfolio(card);
    };

    const showContinueOnCreatorRow = !(isGuidedMasterclass && selectedRole === 'mentor');
    const continueProgramButtonSx = {
      px: '10px',
      py: '4px',
      flexShrink: 0,
      background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
      borderRadius: '4px',
      color: '#FDFDFD',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'none' as const
    };
    // L-11: the CTA persists while any lesson in the program is not green,
    // then disappears — payload-driven, no local state.
    const unresolvedLessons = selectedRole === 'mentee' ? resolveUnresolvedLessons(card) ?? 0 : 0;
    const handleImproveClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handleImprovePerformance(card);
    };
    const renderProgramCreatorWithOptionalContinue = () => (
      <Box
        sx={{
          alignSelf: 'stretch',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          flexShrink: 0,
          pt: '4px',
          minWidth: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
          <Avatar
            src={programCreator.avatar}
            sx={{ width: 25, height: 25, borderRadius: '9999px', fontSize: 12, bgcolor: '#2A2A2A' }}
          >
            {(programCreator.name || '?').trim().charAt(0).toUpperCase()}
          </Avatar>
          <Typography
            sx={{
              color: '#B6B6B6',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.35,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible' }
            }}
          >
            <Box component="span" sx={{ color: '#868686', fontWeight: 400 }}>
              Built by{' '}
            </Box>
            {programCreator.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {unresolvedLessons > 0 && (
            <Button
              onClick={handleImproveClick}
              sx={{
                ...continueProgramButtonSx,
                background: 'transparent',
                border: '1px solid #FF6A55',
                color: '#FF6A55'
              }}
            >
              Retakes
            </Button>
          )}
          {showContinueOnCreatorRow ? (
            <Button onClick={handleContinueClick} sx={continueProgramButtonSx}>
              Open Program
            </Button>
          ) : null}
        </Box>
      </Box>
    );

    // tenta ler progress se existir
    const anyEntry: any = card.entries[0] || {};
    const rawProgress = anyEntry?.progress ?? anyEntry?.programProgress ?? anyEntry?.completionPercent ?? null;
    const progressValue =
      typeof rawProgress === 'number'
        ? Math.max(0, Math.min(100, rawProgress))
        : columnStatus === 'completed'
        ? 100
        : undefined;

    const isCompleted = columnStatus === 'completed';
    const taskSummary = cardTaskSummaries[card.id];
    const summaryPercent =
      taskSummary && taskSummary.totalLevels > 0
        ? Math.round((taskSummary.greenLevels / taskSummary.totalLevels) * 100)
        : undefined;
    const payloadProgress = resolveProgramProgressFromCard(card);
    const displayPercent =
      (isMasterclassProgram || isGuidedMasterclass ? payloadProgress : undefined) ?? summaryPercent ?? progressValue;
    const timeRemainingMinutesFromPayload = resolveTimeRemainingMinutesFromCard(card);
    const isNotStarted = columnStatus === 'not-started';
    const timeRemainingText = isNotStarted
      ? 'Not started'
      : timeRemainingMinutesFromPayload !== undefined && timeRemainingMinutesFromPayload > 0
      ? `${timeRemainingMinutesFromPayload} minutes left`
      : taskSummary && taskSummary.timeRemainingMinutes > 0
      ? `${taskSummary.timeRemainingMinutes} minutes left`
      : '';
    const { remainingObjectives, totalObjectives, hasObjectiveInfo } = getCardObjectivesInfo(card);
    const mentorshipProgressFromObjectives =
      isMentorshipProgram && hasObjectiveInfo && (totalObjectives ?? 0) > 0
        ? Math.round(((totalObjectives - remainingObjectives) / totalObjectives) * 100)
        : undefined;
    const progressPercent = Math.max(
      0,
      Math.min(
        100,
        typeof displayPercent === 'number'
          ? displayPercent
          : typeof mentorshipProgressFromObjectives === 'number'
          ? mentorshipProgressFromObjectives
          : 0
      )
    );
    const statusLabel = card.status || (isCompleted ? 'Completed' : 'In progress');
    const statusDisplayLabel =
      statusLabel === 'in-progress' ? 'In progress' : statusLabel === 'not-started' ? 'Not started' : statusLabel;
    const showMentorshipMenteeObjectives = isMentorshipProgram && selectedRole === 'mentee' && hasObjectiveInfo;
    const progressBar = (
      <Box
        sx={{
          alignSelf: 'stretch',
          height: 6,
          background: '#2A2A2A',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: `${progressPercent}%`,
            height: '100%',
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)'
          }}
        />
      </Box>
    );
    const renderMentorStatsFooter = () => {
      const cardStats = mentorCardStats[card.id];
      const messagesSentCount = cardStats?.messagesSent ?? mentorStats.messagesSent;
      const menteesEngagedCount = cardStats?.menteesEngaged ?? mentorStats.menteesEngaged;
      const meetingsScheduledCount = cardStats?.meetingsScheduled ?? mentorStats.futureMeetings;
      return (
        <Box sx={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {isMentorshipProgram && columnStatus === 'in-progress' && progressBar}
          <Box
            sx={{
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              gap: '10px'
            }}
          >
            <Box
              sx={{
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px'
              }}
            >
              <Box sx={{ color: '#868686', fontSize: 14, fontWeight: 400 }}>{messagesSentCount} Messages Shared</Box>
              <Box sx={{ color: '#868686', fontSize: 14, fontWeight: 400 }}>
                {menteesEngagedCount} {menteesWord} Engaged
              </Box>
              <Box sx={{ color: '#868686', fontSize: 14, fontWeight: 400 }}>
                {meetingsScheduledCount} Meetings Scheduled
              </Box>
            </Box>
          </Box>
        </Box>
      );
    };
    const renderMentorshipMenteeFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px'
          }}
        >
          <Box sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400 }}>{statusDisplayLabel}</Box>
          {(columnStatus === 'in-progress' ||
            String(statusLabel || '')
              .toLowerCase()
              .replace(/\s+/g, '-') === 'in-progress') &&
            progressBar}
        </Box>
        <Box sx={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', minHeight: 32 }}>
          <Box sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600 }}>
            {remainingObjectives} Objectives yet to be completed
          </Box>
        </Box>
      </>
    );
    const renderGuidedMenteeFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px'
          }}
        >
          <Box sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400 }}>{`Program Progress: ${progressPercent}%`}</Box>
          {progressBar}
        </Box>
        <Box sx={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', minHeight: 32 }}>
          <Box sx={{ color: '#868686', fontSize: 12, fontWeight: 600 }}>{timeRemainingText}</Box>
        </Box>
      </>
    );
    const renderMasterclassMenteeFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flexShrink: 0
          }}
        >
          <Box sx={{ alignSelf: 'stretch', color: '#B6B6B6', fontSize: 14, fontWeight: 400, minWidth: 0 }}>
            {timeRemainingText}
          </Box>
          {progressBar}
        </Box>
      </>
    );
    const renderDefaultFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px'
          }}
        >
          <Typography
            sx={{
              flex: '1 1 0',
              color: '#B6B6B6',
              fontSize: 14,
              fontWeight: 400
            }}
          >
            {statusDisplayLabel}
          </Typography>
          {progressBar}
        </Box>
      </>
    );

    return (
      <Box
        key={`${card.id}-${columnStatus}`}
        sx={{
          width: '100%',
          alignSelf: 'stretch',
          flexShrink: 0,
          boxSizing: 'border-box',
          minHeight: 208,
          p: '20px',
          background: '#151515',
          borderRadius: '5px',
          outline: '2px solid #2A2A2A',
          outlineOffset: '-2px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          justifyContent:
            columnStatus === 'in-progress' || columnStatus === 'not-started' ? 'flex-start' : 'space-between',
          opacity: isCompleted ? 0.4 : 1,
          cursor: 'pointer'
        }}
        onClick={() => handleOpenDrawer(card)}
      >
        {/* HEADER + TITLE */}
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <Box
            sx={{
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            {/* TAG */}
            <Box
              sx={{
                px: '8px',
                py: '3px',
                borderRadius: '20px',
                outline: `1px solid ${borderColor}`,
                outlineOffset: '-1px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: borderColor
              }}
            >
              <Typography
                sx={{
                  color: textColor,
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: '14px'
                }}
              >
                {tagLabel}
              </Typography>
            </Box>

            {showMenteeMcqBadge ? (
              <Box
                sx={{
                  px: '8px',
                  py: '5px',
                  borderRadius: '6px',
                  background: '#2A2A2A',
                  outline: '1px solid #444444',
                  outlineOffset: '-1px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  minHeight: 26
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: '#FDFDFD',
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    m: 0,
                    display: 'block'
                  }}
                >
                  Quiz Score: {menteeMcqNotRequired ? NOT_REQUIRED : `${menteeMcqPercent}%`}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Typography
                    sx={{
                      color: '#868686',
                      fontSize: 12,
                      fontWeight: 400
                    }}
                  >
                    Role:
                  </Typography>
                  {renderRoleToggle(card)}
                </Box>
              </Box>
            )}
          </Box>

          {/* TITLE */}
          <Typography
            sx={{
              alignSelf: 'stretch',
              color: '#FDFDFD',
              fontSize: 22,
              fontWeight: 600,
              textTransform: 'capitalize',
              lineHeight: '26.4px'
            }}
          >
            {card.name}
          </Typography>
        </Box>

        <Box sx={{ alignSelf: 'stretch', height: '1px', background: '#444444', flexShrink: 0 }} />

        {isMentorshipProgram && selectedRole === 'mentor'
          ? renderMentorStatsFooter()
          : isMentorshipProgram && selectedRole === 'mentee'
          ? renderMentorshipMenteeFooter()
          : isGuidedMasterclass && selectedRole === 'mentee'
          ? renderGuidedMenteeFooter()
          : isMasterclassProgram && selectedRole === 'mentee'
          ? renderMasterclassMenteeFooter()
          : isGuidedMasterclass && selectedRole === 'mentor'
          ? renderMentorStatsFooter()
          : renderDefaultFooter()}

        {renderProgramCreatorWithOptionalContinue()}
      </Box>
    );
  };

  const renderPlaceholderCard = (key: string) => (
    <Skeleton
      key={`placeholder-${key}`}
      variant="rounded"
      sx={{ width: '100%', minHeight: 208, flexShrink: 0, borderRadius: '5px', boxSizing: 'border-box' }}
    />
  );

  const columns: { key: ProgramStatusKey; title: string }[] = [
    { key: 'not-started', title: 'Not started' },
    { key: 'in-progress', title: 'In progress' },
    { key: 'completed', title: 'Completed' }
  ];


  const drawerCardModel = drawerProgram ? getCardModel(drawerProgram) || '' : '';
  const drawerProgramTag = drawerProgram
    ? getProgramTag(drawerProgram, drawerCardModel)
    : { label: 'Mentorship Program', borderColor: '#8E44FF' };
  const isMentorDrawer = drawerRole === 'mentor';
  const isMenteeDrawer = drawerRole === 'mentee';

  const drawerTaskSummary = drawerProgram ? cardTaskSummaries[drawerProgram.id] : undefined;
  const drawerPayloadProgress = resolveProgramProgressFromCard(drawerProgram);
  const drawerPercent =
    (drawerProgramTag.label === 'Course' || drawerProgramTag.label === 'Guided Course'
      ? drawerPayloadProgress
      : undefined) ??
    (drawerTaskSummary && drawerTaskSummary.totalLevels > 0
      ? Math.round((drawerTaskSummary.greenLevels / drawerTaskSummary.totalLevels) * 100)
      : undefined);
  const drawerTimeRemainingMinutes = resolveTimeRemainingMinutesFromCard(drawerProgram);
  // The figure the pathway card shows: the API's time left. With none (a course nobody has started
  // has timeLeft 0) the drawer shows none either. It used to fall back to "remaining tasks × 4
  // minutes" and then to a made-up "~ 25 minutes", so card and drawer disagreed
  // (docs/BUG-learner-course-time-left-disagrees.md).
  const drawerTimeRemaining =
    drawerTimeRemainingMinutes !== undefined && drawerTimeRemainingMinutes > 0
      ? `${drawerTimeRemainingMinutes} minutes left`
      : '';
  const drawerCardModelText = drawerCardModel ? drawerCardModel.toString().toLowerCase() : '';
  const isMasterclassMentee = isMenteeDrawer && drawerCardModelText.includes('masterclass');
  // Whether we can name the lesson this CTA opens. Only the Learning Lifecycle
  // stub carries `resumeLesson` — getMemberLearningFramework is the one endpoint
  // that computes it — so a drawer opened from this page's own cards never has
  // it. A missing one therefore means "we cannot name the next lesson here",
  // not "there is no next lesson": the button then opens the program, which is
  // what handleNextLesson already falls back to, and the label says so rather
  // than promising a lesson. Only a locked program has nowhere to go at all.
  const canOpenNextLesson = Boolean(
    drawerProgram?.raw?.resumeLesson?.moduleId && drawerProgram?.raw?.resumeLesson?.taskId
  );
  const drawerProgramLocked = drawerProgram?.raw?.locked === true;
  const isMentorshipMentee = isMenteeDrawer && drawerCardModelText.includes('mentorship');
  const isMentorshipMentor = isMentorDrawer && drawerCardModelText.includes('mentorship');

  const isGuidedMasterclassCard = drawerProgramTag.label === 'Guided Course';
  const isGuidedMasterclassMentor = isGuidedMasterclassCard && isMentorDrawer;
  const isGuidedMasterclassMentee = isGuidedMasterclassCard && isMenteeDrawer;
  const guidedMasterclassMentors = useMemo(() => {
    if (!isGuidedMasterclassMentee) return [];
    const seen = new Set<string>();
    const mentors: any[] = [];
    Object.values(objectiveMatchesMap || {}).forEach((matches) => {
      (Array.isArray(matches) ? matches : []).forEach((match: any) => {
        const mentor = match?.mentor || match;
        const matchId = mentor?.id || mentor?.userId || mentor?.mid || mentor?.matchId || mentor?._id || mentor?.uuid;
        if (!matchId || matchId === currentUserId || seen.has(matchId)) return;
        seen.add(matchId);
        mentors.push({
          ...mentor,
          matchedObjectives: match?.matchedObjectives || match?.extended?.learningObjectives || [],
          status: match?.status || match?.matchStatus || match?.matchStatusText || mentor?.status || 'Ongoing'
        });
      });
    });
    return mentors;
  }, [objectiveMatchesMap, isGuidedMasterclassMentee, currentUserId]);

  const showLeaderboard = drawerCardModelText.includes('masterclass');
  const leaderboardEntries = useMemo(() => {
    const source = leaderboardPeers.length ? leaderboardPeers : menteeList;
    const list = (source || [])
      .map((mentee) => {
        const progress = resolveGoalProgress(mentee);
        return {
          id: mentee?.mid || mentee?.userId || mentee?.id || mentee?.menteeId,
          displayName: mentee?.displayName || mentee?.name || mentee?.menteeName || `Unnamed ${menteeWordLower}`,
          headshot: mentee?.headshot || mentee?.photo || mentee?.picture || undefined,
          goalProgress: progress,
          MCQScore: mentee.MCQScore,
          source: mentee
        };
      })
      .sort((a, b) => (b.goalProgress ?? 0) - (a.goalProgress ?? 0));

    if (drawerRole === 'mentee' && currentUserId) {
      const alreadyIncluded = list.some((entry) => entry.id && entry.id === currentUserId);
      if (!alreadyIncluded) {
        const myProgress = resolveGoalProgress(cardMenteeProgress[learningGrpId] || {});
        const myEntry = {
          id: currentUserId,
          displayName: getUserFriendlyName(userDetails),
          headshot: userDetails?.picture || userDetails?.photo || undefined,
          goalProgress: Math.min(Math.max(myProgress ?? 0, 0), 100),
          MCQScore: 0,
          isCurrentUser: true,
          source: { user: currentUserId }
        };
        list.push(myEntry);
      }
    }

    return list.sort((a, b) => (b.goalProgress ?? 0) - (a.goalProgress ?? 0));
  }, [
    leaderboardPeers,
    menteeList,
    drawerRole,
    currentUserId,
    learningGrpId,
    cardMenteeProgress,
    userDetails,
    menteeWordLower
  ]);

  // When mounted hidden by the LL host we MUST fall through to the
  // main return — the MUI Drawer (portal'd to body) lives inside that
  // JSX. Early-returning to a spinner would either show the spinner on
  // top of the LL page (hidden=false branch) or unmount the drawer
  // (return null branch), neither of which we want. The body of the
  // dashboard is invisible thanks to the `hidden` prop applied to the
  // root Box; the drawer opens immediately via the snappy stub path
  // and shows its own loading state inside for in-flight fetches.
  if (!hidden && groupRoleState.loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', ...fadeInSx }}>
        <FrameworkColumnsSkeleton cardHeight={208} columns={3} cards={2} />
      </Box>
    );
  }

  if (!hidden && credentialsReady && !apiProgramsHydrated) {
    return (
      <Box sx={{ height: '100%', display: 'flex', ...fadeInSx }}>
        <FrameworkColumnsSkeleton cardHeight={208} columns={3} cards={2} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        p: '25px',
        // `hidden` collapses the dashboard body so callers that mount
        // this component just for its expanded program Drawer (LL
        // Member dashboard) don't double-up the page. The Drawer is a
        // MUI portal to document.body so it stays visible.
        display: hidden ? 'none' : 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        // gap: '80px',
        background: '#1C1C1C',
        borderRadius: '8px',
        outline: '1px solid #2A2A2A',
        outlineOffset: '-1px',
        boxSizing: 'border-box'
      }}
    >
      {columns.map((col, index) => {
        const cardsInColumn = grouped.filter((card) => {
          const status = getEffectiveStatus(card);
          return status === col.key;
        });
        return (
          <Box
            key={col.key}
            sx={{
              flex: '1 1 0',
              alignSelf: 'stretch',
              borderRadius: '5px',
              borderRight: index < columns.length - 1 ? '1px solid #2A2A2A' : 'none',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* HEADER DA COLUNA */}
            <Box
              sx={{
                alignSelf: 'stretch',
                px: '20px',
                py: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Typography
                  sx={{
                    color: '#B6B6B6',
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: '19.88px'
                  }}
                >
                  {col.title}
                </Typography>
                <InfoOutlinedIcon sx={{ color: '#B6B6B6', fontSize: 18 }} />
              </Box>
            </Box>

            {/* LISTA DE CARDS */}
            <Box
              sx={{
                alignSelf: 'stretch',
                flex: '1 1 0',
                p: '10px',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                overflow: 'auto',
                height: '100%'
              }}
            >
              {cardsInColumn.map((card) =>
                dashboardReady && cardReady[card.id]
                  ? renderCard(card, getEffectiveStatus(card))
                  : renderPlaceholderCard(card.id)
              )}
            </Box>
          </Box>
        );
      })}

      {/* DRAWER DETALHES DO PROGRAMA */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 600,
            background: '#151515 !important',
            borderLeft: '1px solid #2A2A2A !important',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            // Phone: the program's screen, full width, clear of the notch and the home indicator.
            [MOBILE_SX]: {
              width: '100%',
              borderLeft: 'none !important',
              pt: 'env(safe-area-inset-top)',
              pb: 'env(safe-area-inset-bottom)'
            }
          }
        }}
      >
        {drawerProgram && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              px: '25px !important',
              py: '20px !important',
              display: 'flex',
              flexDirection: 'column',
              gap: '30px !important',
              overflowY: 'auto',
              [MOBILE_SX]: { px: '16px !important', py: '12px !important', gap: '20px !important' }
            }}
          >
            {drawerLoading ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 280
                }}
              >
                <CircularProgress sx={{ color: '#F79420' }} size={40} />
              </Box>
            ) : (
              <>
                {/* HEADER */}
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {/* TAG */}
                  <Box
                    sx={{
                      px: '8px',
                      py: '3px',
                      background: drawerProgramTag.borderColor,
                      borderRadius: '20px',
                      fontSize: 12,
                      color: drawerProgramTag.label === 'Course' ? 'black' : '#FDFDFD',
                      fontFamily: 'Inter'
                    }}
                  >
                    {drawerProgramTag.label}
                  </Box>

                  {/* ROLE */}
                  {!isMasterclassMentee && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Program Role:</Typography>
                      <Box
                        sx={{
                          px: '8px',
                          py: '5px',
                          background: '#444',
                          borderRadius: '6px',
                          color: '#FDFDFD',
                          fontSize: 10,
                          fontWeight: 700
                        }}
                      >
                        {roleLabel(drawerRole)}
                      </Box>
                    </Box>
                  )}

                  {/* CLOSE ICON */}
                  <IconButton
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      ml: 'auto',
                      width: 32,
                      height: 32,
                      color: '#FDFDFD',
                      border: '1px solid #444',
                      borderRadius: '6px'
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>

                {/* TITLE */}
                <Typography
                  sx={{
                    color: '#FDFDFD',
                    fontSize: 26,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    [MOBILE_SX]: { fontSize: 22, lineHeight: 1.25 }
                  }}
                >
                  {drawerProgram.name}
                </Typography>

                {/* ROLES BOX */}
                <Box
                  sx={{
                    width: '100%',
                    p: '15px',
                    background: '#0F0F0F',
                    borderRadius: '5px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px'
                  }}
                >
                  {drawerProgram.roles.length > 1 && (
                    <>
                      <Typography
                        sx={{
                          color: '#F79420',
                          fontSize: 12,
                          fontWeight: 700,
                          textAlign: 'center'
                        }}
                      >
                        You hold two roles in this program, please select which to view.
                      </Typography>

                      {/* ROLE SELECTOR */}
                      <Box
                        sx={{
                          width: '100%',
                          overflow: 'hidden',
                          borderRadius: '6px',
                          display: 'flex'
                        }}
                      >
                        {drawerProgram.roles.map((role) => {
                          const isSelected = drawerRole === role;
                          return (
                            <Box
                              key={role}
                              onClick={() => {
                                setDrawerRole(role);
                                setRoleSelections((prev) => ({ ...prev, [drawerProgram.id]: role }));
                                syncReduxForRole(drawerProgram, role);
                              }}
                              sx={{
                                flex: 1,
                                px: '18px',
                                py: '9px',
                                background: isSelected ? '#444444' : '#2A2A2A',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: isSelected ? 700 : 400,
                                  color: isSelected ? '#FDFDFD' : '#B6B6B6'
                                }}
                              >
                                {roleLabel(role)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}

                  {/* DESCRIPTION + FLAG ICON */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FlagOutlinedIcon sx={{ color: '#FDFDFD', fontSize: 18 }} />
                    <Typography sx={{ color: '#FDFDFD', fontSize: 10, fontWeight: 700, fontFamily: 'Inter' }}>
                      {/* {drawerRole === 'mentor'
                    ? 'Find mentees whose objectives align with your expertise, message each one and schedule a mentorship meeting with them.'
                    : 'Find a mentor for each objective, message them, and complete at least one meeting with each.'} */}
                      {isMentorshipMentee &&
                        `Your goal is to message your ${mentorWordLower}(s), and complete at least one meeting with them for each Objective`}
                      {isMasterclassMentee &&
                        'Your goal is to complete each lesson by reading your Lesson Body, completing your lesson assessment, and marking your lesson as complete.'}
                      {isGuidedMasterclassMentor &&
                        `Your goal is to guide ${menteeWordLower} matches through the completion of their program by messaging them to offer help and joining mentorship meetings where helpful.`}
                      {isMentorshipMentor &&
                        `If you don’t hear from your ${menteeWordLower}, message them and encourage them to schedule a meeting with you.`}
                    </Typography>
                  </Box>
                </Box>

                {/* CTA BUTTON */}
                <Button
                  fullWidth
                  // A locked program answers 423 on every read, so there is
                  // nowhere to send the learner; everything else has a real
                  // destination and the label below says which.
                  disabled={isMasterclassMentee && drawerProgramLocked}
                  onClick={() => (isMasterclassMentee ? handleNextLesson() : handleManagePortfolio())}
                  sx={{
                    px: '14px',
                    py: '10px',
                    borderRadius: '6px',
                    background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                    color: '#FDFDFD',
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&.Mui-disabled': {
                      background: '#3A3A3A',
                      color: 'rgba(253, 253, 253, 0.45)'
                    }
                  }}
                >
                  {isMasterclassMentee
                    ? canOpenNextLesson
                      ? 'Next Lesson'
                      : 'Open Program'
                    : isMentorshipMentee
                    ? 'Open Objectives'
                    : isGuidedMasterclassMentor
                    ? `View ${menteesWord}`
                    : 'Manage Portfolio'}
                </Button>

                {isMasterclassMentee && (
                  <Box
                    sx={{
                      background: '#0F0F0F'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1
                      }}
                    >
                      <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                        {drawerPercent !== undefined ? `Program Progress: ${drawerPercent}%` : 'Program Progress'}
                      </Typography>
                      {drawerPercent !== undefined ? (
                        <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>
                          {drawerPercent}%
                        </Typography>
                      ) : (
                        <CircularProgress size={12} sx={{ color: '#868686' }} />
                      )}
                    </Box>
                    {drawerPercent !== undefined ? (
                      <Box
                        sx={{
                          height: '5px',
                          borderRadius: '3px',
                          background: '#2A2A2A',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.min(Math.max(drawerPercent, 0), 100)}%`,
                            background: '#F0B80D',
                            borderRadius: '3px'
                          }}
                        />
                      </Box>
                    ) : (
                      // Skeleton bar — keeps layout stable while the per-card
                      // task summary fetch is in flight, avoids the glitch
                      // where the bar showed 0% then snapped to the real
                      // value once data arrived.
                      <Skeleton variant="rectangular" height={5} sx={{ borderRadius: '3px', bgcolor: '#2A2A2A' }} />
                    )}
                    {drawerPercent !== undefined && drawerTimeRemaining && (
                      <Typography sx={{ color: '#B6B6B6', fontSize: 12, mt: 1 }}>{drawerTimeRemaining}</Typography>
                    )}
                    {drawerPercent === undefined && (
                      <Skeleton variant="text" width={180} height={16} sx={{ mt: 1, bgcolor: '#2A2A2A' }} />
                    )}
                  </Box>
                )}

                {isMentorDrawer && (
                  <>
                    {drawerLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                          My Mentorship Stats:
                        </Typography>
                        {renderDrawerSkeletonLines(3)}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                          My Mentorship Stats:
                        </Typography>

                        {mentorshipStatRows.map((row, idx) => (
                          <Box key={idx} sx={{ display: 'flex', gap: '20px' }}>
                            {row.map((stat) => (
                              <Box key={stat.label} sx={{ flex: 1, p: '5px' }}>
                                <Typography
                                  sx={{
                                    color: '#B6B6B6',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: '14.40px'
                                  }}
                                >
                                  {stat.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#FDFDFD',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    lineHeight: '21.60px'
                                  }}
                                >
                                  {stat.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                  </>
                )}
                {isMenteeDrawer && !isMasterclassMentee && (
                  <>
                    {drawerLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                          My Mentorship Stats:
                        </Typography>
                        {renderDrawerSkeletonLines(3)}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                          My Mentorship Stats:
                        </Typography>

                        {menteeStatRows.map((row, idx) => (
                          <Box key={idx} sx={{ display: 'flex', gap: '20px' }}>
                            {row.map((stat) => (
                              <Box key={stat.label} sx={{ flex: 1, p: '5px' }}>
                                <Typography
                                  sx={{
                                    color: '#B6B6B6',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: '14.40px'
                                  }}
                                >
                                  {stat.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#FDFDFD',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    lineHeight: '21.60px'
                                  }}
                                >
                                  {stat.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                  </>
                )}
                {showLeaderboard && (
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ width: '100%', height: '1px', background: '#444', mb: '15px' }} />
                    <Leaderboard
                      data={leaderboardEntries}
                      loader={leaderboardLoading && leaderboardEntries.length === 0}
                    />
                    <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                  </Box>
                )}
                {isGuidedMasterclassMentor ? (
                  drawerLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>My {menteesWord}</Typography>
                      {renderDrawerSkeletonLines(3)}
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                          My {menteesWord}
                        </Typography>
                        {(menteeList?.length || 0) === 0 ? (
                          <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                            No {w('mentee', { plural: true, lower: true })} matched yet.
                          </Typography>
                        ) : (
                          (menteeList || []).map((mentee, index) => {
                            const matchId =
                              mentee?.id ||
                              mentee?.menteeId ||
                              mentee?.userId ||
                              mentee?.matchId ||
                              `${mentee?.displayName || 'mentee'}-${mentee?._id || index}`;
                            const avatar = mentee?.headshot || mentee?.photo || mentee?.picture || undefined;
                            const name =
                              mentee?.name || mentee?.displayName || mentee?.menteeName || `Unnamed ${menteeWordLower}`;
                            const statusValue =
                              mentee?.status || mentee?.matchStatus || mentee?.matchStatusText || 'in-progress';
                            const statusLabel = getStatusText(statusValue);
                            const statusColor = getStatusColor(statusValue);
                            const objectiveTitle =
                              (mentee?.matchedObjectives || [])[0]?.objective ||
                              (mentee?.matchedObjectives || [])[0]?.name ||
                              'Match ativo';
                            const completionPercent = getCompletionPercent(mentee);
                            return (
                              <Box
                                key={matchId}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '6px',
                                  py: '12px'
                                  // borderBottom: '1px solid #2A2A2A'
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box
                                      component="img"
                                      src={avatar}
                                      alt={name}
                                      sx={{ width: 44, height: 44, borderRadius: '50%' }}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <Typography
                                        sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}
                                      >
                                        {name}
                                      </Typography>
                                      {/* <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography> */}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* <Typography sx={{ color: '#B6B6B6', fontSize: 11, textTransform: 'uppercase' }}>
                                  {`${completionPercent}% completo`}
                                </Typography> */}
                                    {/* <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    color: '#FDFDFD',
                                    // borderColor: '#444444',
                                    textTransform: 'none',
                                    // borderRadius: '50%',
                                    fontSize: 11,
                                    height: '20px'  ,
                                    width: '20px'  ,
                                    // px: 1.5,
                                    // py: 0.25
                                  }}
                                  onClick={() => handleStartConversation(mentor)}
                                > */}
                                    <Box
                                      onClick={() => handleStartConversation(mentee)}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        padding: '7px',
                                        background:
                                          'linear-gradient(180deg, var(--orange-500, #F79420) 0%, var(--orange-700, #F15A26) 100%)',
                                        borderRadius: '17px',
                                        display: 'inline-flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.9 }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: '20px',
                                          height: '20px',
                                          padding: '2px',
                                          position: 'relative',
                                          overflow: 'hidden',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center'
                                        }}
                                      >
                                        {/* Ícone no centro — agora visível e colorido */}
                                        <QuestionAnswerOutlinedIcon
                                          sx={{
                                            zIndex: 2,
                                            fontSize: '18px',
                                            color: 'white' // <<< COR DO ÍCONE DEFINIDA AQUI
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    {/* </Button> */}
                                    <Box
                                      onClick={(event) => handleOpenMenu(event, mentee)}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        padding: '7px',
                                        background: 'var(--Fill-Button-button-contrast, #CDCDCD)',
                                        borderRadius: '17px',
                                        display: 'inline-flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.9 }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: '20px',
                                          height: '20px',
                                          padding: '2px',
                                          position: 'relative',
                                          overflow: 'hidden',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center'
                                        }}
                                      >
                                        {/* Ícone no centro — agora visível e colorido */}
                                        <MoreHorizIcon
                                          sx={{
                                            zIndex: 2,
                                            fontSize: '18px',
                                            color: '#0F0F0F' // <<< COR DO ÍCONE DEFINIDA AQUI
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    {/* <IconButton
                                  size="small"
                                  onClick={(event) => handleOpenMenu(event, mentor)}
                                  sx={{ color: '#FDFDFD' }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton> */}
                                  </Box>
                                </Box>
                                {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography
                                sx={{
                                  color: statusColor || '#868686',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}
                              >
                                {statusLabel}
                              </Typography>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                            </Box> */}
                                {/* <LinearProgress
                              variant="determinate"
                              value={completionPercent}
                              sx={{
                                height: 4,
                                borderRadius: '999px',
                                backgroundColor: '#0F0F0F',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#F79420'
                                }
                              }}
                            /> */}
                              </Box>
                            );
                            return (
                              <Box
                                key={matchId}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '6px',
                                  py: '12px',
                                  borderBottom: '1px solid #2A2A2A'
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box
                                      component="img"
                                      src={avatar}
                                      alt={name}
                                      sx={{ width: 44, height: 44, borderRadius: '50%' }}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
                                        {name}
                                      </Typography>
                                      <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ color: '#B6B6B6', fontSize: 11, textTransform: 'uppercase' }}>
                                      {`${completionPercent}% completo`}
                                    </Typography>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        color: '#FDFDFD',
                                        borderColor: '#444444',
                                        textTransform: 'none',
                                        fontSize: 11,
                                        px: 1.5,
                                        py: 0.25
                                      }}
                                      onClick={() => handleStartConversation(mentee)}
                                    >
                                      Start Conversation
                                    </Button>
                                    <IconButton
                                      size="small"
                                      onClick={(event) => handleOpenMenu(event, mentee)}
                                      sx={{ color: '#FDFDFD' }}
                                    >
                                      <MoreHorizIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography
                                    sx={{
                                      color: statusColor || '#868686',
                                      fontSize: 12,
                                      fontWeight: 600,
                                      textTransform: 'uppercase'
                                    }}
                                  >
                                    {statusLabel}
                                  </Typography>
                                  <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={completionPercent}
                                  sx={{
                                    height: 4,
                                    borderRadius: '999px',
                                    backgroundColor: '#0F0F0F',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: '#F79420'
                                    }
                                  }}
                                />
                              </Box>
                            );
                          })
                        )}
                      </Box>
                      <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                    </>
                  )
                ) : isGuidedMasterclassMentee ? (
                  drawerLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>My {mentorsWord}</Typography>
                      {renderDrawerSkeletonLines(3)}
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, fontFamily: 'Inter' }}>
                          My {mentorsWord}:
                        </Typography>
                        {menteeList.length === 0 ? (
                          <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                            No {w('mentor', { plural: true, lower: true })} matched yet.
                          </Typography>
                        ) : (
                          menteeList.map((mentor, index) => {
                            const matchId =
                              mentor?.id ||
                              mentor?.userId ||
                              mentor?.mid ||
                              mentor?.matchId ||
                              `${mentor?.displayName || 'mentor'}-${mentor?._id || index}`;
                            const avatar = mentor?.headshot || mentor?.photo || mentor?.avatar || undefined;
                            const name = mentor?.name || mentor?.displayName || `Unnamed ${mentorWordLower}`;
                            const statusValue =
                              mentor?.status || mentor?.matchStatus || mentor?.matchStatusText || 'in-progress';
                            const statusLabel = getStatusText(statusValue);
                            const statusColor = getStatusColor(statusValue);
                            const objectiveTitle =
                              (mentor?.matchedObjectives || [])[0]?.objective ||
                              (mentor?.matchedObjectives || [])[0]?.name ||
                              'Active match';
                            const completionPercent = getCompletionPercent(mentor);

                            return (
                              <Box
                                key={matchId}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '6px',
                                  py: '12px'
                                  // borderBottom: '1px solid #2A2A2A'
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box
                                      component="img"
                                      src={avatar}
                                      alt={name}
                                      sx={{ width: 44, height: 44, borderRadius: '50%' }}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <Typography
                                        sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}
                                      >
                                        {name}
                                      </Typography>
                                      {/* <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography> */}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* <Typography sx={{ color: '#B6B6B6', fontSize: 11, textTransform: 'uppercase' }}>
                                  {`${completionPercent}% completo`}
                                </Typography> */}
                                    {/* <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    color: '#FDFDFD',
                                    // borderColor: '#444444',
                                    textTransform: 'none',
                                    // borderRadius: '50%',
                                    fontSize: 11,
                                    height: '20px'  ,
                                    width: '20px'  ,
                                    // px: 1.5,
                                    // py: 0.25
                                  }}
                                  onClick={() => handleStartConversation(mentor)}
                                > */}
                                    <Box
                                      onClick={() => handleStartConversation(mentor)}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        padding: '7px',
                                        background: 'var(--Fill-Button-button-contrast, #CDCDCD)',
                                        borderRadius: '17px',
                                        display: 'inline-flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.9 }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: '20px',
                                          height: '20px',
                                          padding: '2px',
                                          position: 'relative',
                                          overflow: 'hidden',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center'
                                        }}
                                      >
                                        {/* Ícone no centro — agora visível e colorido */}
                                        <QuestionAnswerOutlinedIcon
                                          sx={{
                                            zIndex: 2,
                                            fontSize: '18px',
                                            color: '#0F0F0F' // <<< COR DO ÍCONE DEFINIDA AQUI
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    {/* </Button> */}
                                    <Box
                                      onClick={(event) => handleOpenMenu(event, mentor)}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        padding: '7px',
                                        background: 'var(--Fill-Button-button-contrast, #CDCDCD)',
                                        borderRadius: '17px',
                                        display: 'inline-flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.9 }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: '20px',
                                          height: '20px',
                                          padding: '2px',
                                          position: 'relative',
                                          overflow: 'hidden',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center'
                                        }}
                                      >
                                        {/* Ícone no centro — agora visível e colorido */}
                                        <MoreHorizIcon
                                          sx={{
                                            zIndex: 2,
                                            fontSize: '18px',
                                            color: '#0F0F0F' // <<< COR DO ÍCONE DEFINIDA AQUI
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    {/* <IconButton
                                  size="small"
                                  onClick={(event) => handleOpenMenu(event, mentor)}
                                  sx={{ color: '#FDFDFD' }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton> */}
                                  </Box>
                                </Box>
                                {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography
                                sx={{
                                  color: statusColor || '#868686',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}
                              >
                                {statusLabel}
                              </Typography>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                            </Box> */}
                                {/* <LinearProgress
                              variant="determinate"
                              value={completionPercent}
                              sx={{
                                height: 4,
                                borderRadius: '999px',
                                backgroundColor: '#0F0F0F',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#F79420'
                                }
                              }}
                            /> */}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                      {/* <Box sx={{ width: '100%', height: '1px', background: '#444' }} /> */}
                    </>
                  )
                ) : (isMentorDrawer || isMenteeDrawer) && !isMasterclassMentee ? (
                  drawerLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                          {isMenteeDrawer
                            ? `Learning Objectives and ${mentorsWord}`
                            : `My Expertise and ${menteesWord}:`}
                        </Typography>
                      </Box>
                      {renderDrawerSkeletonLines(4)}
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                            {isMenteeDrawer
                              ? `Learning Objectives and ${mentorsWord}`
                              : `My Expertise and ${menteesWord}:`}
                          </Typography>
                          {isMentorDrawer && (
                            <>
                              <InfoOutlinedIcon sx={{ color: '#B6B6B6', fontSize: 18 }} />
                              <Box
                                sx={{
                                  ml: 'auto',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  cursor: 'pointer'
                                }}
                                onClick={() => setObjectiveModalOpen(true)}
                              >
                                <Typography sx={{ color: '#F79420', fontSize: 12, fontWeight: 600 }}>
                                  Add Expertise
                                </Typography>
                                <AddIcon sx={{ color: '#F79420', fontSize: 16 }} />
                              </Box>
                            </>
                          )}
                          {isMenteeDrawer && (
                            <Box
                              sx={{
                                ml: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                cursor: 'pointer'
                              }}
                              onClick={() => setObjectiveModalOpen(true)}
                            >
                              <Typography sx={{ color: '#F79420', fontSize: 12, fontWeight: 600 }}>
                                Add Learning Objective
                              </Typography>
                              <AddIcon sx={{ color: '#F79420', fontSize: 16 }} />
                            </Box>
                          )}
                        </Box>

                        {displayLearningObjectives.length === 0 ? (
                          <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                            No learning objectives available yet.
                          </Typography>
                        ) : (
                          displayLearningObjectives.map((obj: any, idx: number) => {
                            const objectiveKey =
                              getObjectiveIdentifier(obj) ||
                              obj?.id ||
                              obj?._id ||
                              obj?.objectiveId ||
                              obj?.objective ||
                              obj?.name ||
                              `${obj?.objective || obj?.name || 'objective'}_${idx}`;

                            const objectiveEntry = (isMenteeDrawer ? drawerUserLearningObjectives : existingKeys).find(
                              (entry: any) => {
                                const candidateIds = [
                                  entry?.objectiveId,
                                  entry?.id,
                                  entry?._id,
                                  entry?.objective,
                                  entry?.name
                                ]
                                  .filter(Boolean)
                                  .map((id: any) => String(id));
                                return candidateIds.some((id: string) => id === String(objectiveKey));
                              }
                            );

                            const mentorsFromEntry =
                              isMenteeDrawer && Array.isArray(obj?.mentors)
                                ? obj.mentors.map((mentor: any) => ({
                                    ...mentor,
                                    matchedObjectives: [
                                      {
                                        objective: obj?.objective || obj?.name || '',
                                        id: objectiveKey,
                                        objectiveId: objectiveKey,
                                        status: obj?.status || 'Ongoing'
                                      }
                                    ],
                                    objectiveLabel: obj?.objective || obj?.name || '',
                                    objectiveStatus: obj?.status || 'Ongoing',
                                    objectiveId: objectiveKey
                                  }))
                                : !isMenteeDrawer && objectiveEntry?.mentors?.length
                                ? objectiveEntry.mentors.map((mentor: any) => ({
                                    ...mentor,
                                    matchedObjectives: [
                                      {
                                        objective: objectiveEntry?.objective || objectiveEntry?.name || '',
                                        id: objectiveKey,
                                        objectiveId: objectiveKey,
                                        status: objectiveEntry?.status || 'Ongoing'
                                      }
                                    ],
                                    objectiveLabel: objectiveEntry?.objective || objectiveEntry?.name || '',
                                    objectiveStatus: objectiveEntry?.status || 'Ongoing',
                                    objectiveId: objectiveKey
                                  }))
                                : [];

                            const objectiveKeyStr = String(objectiveKey);
                            const matchesFromObjectiveMap =
                              isMenteeDrawer && objectiveMatchesMap[objectiveKeyStr]?.length
                                ? objectiveMatchesMap[objectiveKeyStr]
                                : [];

                            const menteesPerObjective = isMenteeDrawer
                              ? mentorsFromEntry
                              : mentorsFromEntry.length
                              ? mentorsFromEntry
                              : matchesFromObjectiveMap.length
                              ? matchesFromObjectiveMap
                              : drawerObjectiveMentors[objectiveKeyStr]?.length
                              ? drawerObjectiveMentors[objectiveKeyStr]
                              : getMenteesByObjectiveId(menteeList, objectiveKeyStr);

                            const objId = obj.id;
                            const expanded = !!expandedObjectives[objId];
                            const objectiveStatus = objectiveEntry?.status ?? obj?.status ?? '';
                            const isObjectiveCompleted = ['completed', 'complete', 'done'].includes(
                              String(objectiveStatus).toLowerCase()
                            );

                            return (
                              <Box key={objId} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    justifyContent: 'space-between'
                                  }}
                                  onClick={() =>
                                    setExpandedObjectives((prev) => ({
                                      ...prev,
                                      [objId]: !expanded
                                    }))
                                  }
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <Typography
                                      sx={{
                                        color: '#B6B6B6',
                                        fontSize: 18,
                                        fontWeight: 700
                                      }}
                                    >
                                      {idx + 1}
                                    </Typography>

                                    <Typography
                                      sx={{
                                        color: '#F79420',
                                        fontSize: 12,
                                        fontWeight: 700
                                      }}
                                    >
                                      {obj?.objective || `Objective ${idx + 1}`}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isMenteeDrawer && isObjectiveCompleted && (
                                      <div
                                        className="justify-start text-UI-Success text-xs font-semibold font-['Inter'] leading-4 line-clamp-1"
                                        style={{ color: 'var(--UI-Success, #00972D)' }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Completed
                                      </div>
                                    )}
                                    <KeyboardArrowDownIcon
                                      sx={{
                                        color: '#FDFDFD',
                                        fontSize: 20,
                                        transform: expanded ? 'rotate(180deg)' : 'none',
                                        transition: '0.2s'
                                      }}
                                    />
                                  </Box>
                                </Box>

                                {expanded && (
                                  <Box
                                    sx={{
                                      pl: '30px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '15px'
                                    }}
                                  >
                                    <Button
                                      onClick={() => {
                                        setRecruitObjId(objId);
                                        setRecruitObjectiveName(obj?.objective || `Objective ${idx + 1}`);
                                        setRecruitModalOpen(true);
                                      }}
                                      sx={{
                                        width: 'fit-content',
                                        background: '#1C1C1C',
                                        color: '#FDFDFD',
                                        fontSize: 12,
                                        border: '1px solid #2A2A2A',
                                        borderRadius: '6px',
                                        textTransform: 'none',
                                        px: 2,
                                        py: 0.75
                                      }}
                                    >
                                      Find New {isMenteeDrawer ? w('mentor') : w('mentee')}
                                    </Button>

                                    {menteesPerObjective.length === 0 ? (
                                      <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                                        No {w(isMenteeDrawer ? 'mentor' : 'mentee', { plural: true, lower: true })}{' '}
                                        matched yet.
                                      </Typography>
                                    ) : (
                                      menteesPerObjective.map((match: any) => {
                                        const person =
                                          (isMenteeDrawer ? match?.mentor : match?.mentee) ||
                                          match?.mentor ||
                                          match?.mentee ||
                                          match;
                                        const matchId = person?.id || match?.mid || match?.userId;
                                        const avatar = resolveProfileImage(person, match);
                                        const name =
                                          person?.displayName ||
                                          person?.menteeName ||
                                          person?.name ||
                                          `Unnamed ${w(isMenteeDrawer ? 'mentor' : 'mentee', { lower: true })}`;
                                        const objectiveEntry =
                                          (match?.matchedObjectives || match?.extended?.learningObjectives || []).find(
                                            (x: any) =>
                                              String(x.id || x._id || x.objective || x.name) ===
                                              String(obj.id || obj._id || obj.objective || obj.name)
                                          ) || {};
                                        const status =
                                          objectiveEntry?.status ||
                                          match?.status ||
                                          match?.matchStatus ||
                                          match?.matchStatusText ||
                                          'Ongoing';
                                        const isMenteeObjectiveCompleted = ['completed', 'complete', 'done'].includes(
                                          String(status).toLowerCase()
                                        );

                                        return (
                                          <Box
                                            key={matchId || `${match?.name || 'match'}-${idx}`}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between'
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                              <ChatAvatarComponent
                                                image={avatar}
                                                firstLetter={name?.charAt(0) || ''}
                                                width="40px"
                                                height="40px"
                                                type="noStatus"
                                              />
                                              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                                                {name}
                                              </Typography>
                                            </Box>

                                            {isMenteeObjectiveCompleted ? (
                                              <Typography
                                                sx={{
                                                  color: 'var(--UI-Success, #00972D)',
                                                  fontSize: 12,
                                                  fontWeight: 600,
                                                  fontFamily: 'Inter'
                                                }}
                                              >
                                                Completed
                                              </Typography>
                                            ) : (
                                              <DosenButton
                                                sizeVariant="sm"
                                                tone="cta"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleStartConversation(person);
                                                }}
                                                sx={{ minWidth: 'auto', px: 1.5, py: 0.75 }}
                                              >
                                                <ChatIcon size={16} color="white" />
                                              </DosenButton>
                                            )}
                                          </Box>
                                        );
                                      })
                                    )}
                                  </Box>
                                )}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                      <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                    </>
                  )
                ) : (
                  // <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                  <div />
                )}
              </>
            )}
          </Box>
        )}
      </Drawer>

      {/* CONTEXT MENU */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        {isMentorDrawer && <MenuItem onClick={handleViewMenteeProgress}>View {menteeWordLower} progress</MenuItem>}
        <MenuItem
          onClick={() => {
            if (menuMentee) handleViewProfile(menuMentee);
          }}
        >
          View {w(isMenteeDrawer ? 'mentor' : 'mentee', { lower: true })} profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuMentee) handleMeetingHistory(menuMentee);
          }}
        >
          Meeting history
        </MenuItem>
      </Menu>

      <Modal open={meetingModalOpen} onClose={handleCloseMeetingModal}>
        <Box
          sx={{
            width: 500,
            maxHeight: '80vh',
            overflowY: 'auto',
            bgcolor: 'var(--Fill-Modal-background, #0F0F0F)',
            borderRadius: 2,
            outline: '1px solid var(--Stroke-Modal-border, #444444)',
            outlineOffset: '-1px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 3
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, mb: 2 }}>
            Meeting History with {meetingMentee?.name || meetingMentee?.displayName || meetingMentee?.menteeName || ''}
          </Typography>
          {(meetingMentee?.meetings || []).length ? (
            (meetingMentee.meetings || []).map((meeting: any, idx: number) => (
              <Box key={`${meeting?.id || idx}-${meetingMentee?.id || 'mentee'}`} mb={2}>
                <MeetingDetails
                  meeting={meeting}
                  index={idx + 1}
                  menteeName={meetingMentee?.name || meetingMentee?.displayName || meetingMentee?.menteeName || ''}
                  myMenteesList={meetingMentee}
                  mentorsList={[]}
                  menteeData={meetingMentee}
                />
              </Box>
            ))
          ) : (
            <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>No meeting history available.</Typography>
          )}
        </Box>
      </Modal>

      <UserProfileDetailsPopup
        loading={false}
        userData={profileUser}
        openUserProfileDetails={profileModalOpen}
        handleopenUserProfileDetails={(flag: boolean) => setProfileModalOpen(flag)}
      />

      {/* MODAL DE OBJETIVO / EXPERTISE */}
      <Modal open={objectiveModalOpen} onClose={() => setObjectiveModalOpen(false)}>
        <Box
          sx={{
            width: 550,
            bgcolor: 'var(--Fill-Modal-background, #0F0F0F)',
            borderRadius: 2,
            outline: '1px solid var(--Stroke-Modal-border, #444444)',
            outlineOffset: '-1px',
            overflow: 'hidden',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <Box
            sx={{
              width: '100%',
              px: 3,
              pr: 5,
              py: 2.5,
              borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              position: 'relative'
            }}
          >
            <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
              {drawerRole === 'mentor' ? 'Add Area of Expertise' : 'Add Learning Objective'}
            </Typography>
            <Button
              onClick={() => {
                setObjectiveModalOpen(false);
                setSelectedObjectiveId('');
              }}
              sx={{
                minWidth: 32,
                height: 32,
                color: '#FDFDFD',
                border: '1px solid #444',
                borderRadius: '6px'
              }}
            >
              X
            </Button>
          </Box>
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5
            }}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 500 }}>
                {drawerRole === 'mentor' ? 'Area of Expertise *' : 'Learning Objective *'}
              </Typography>
              <Select
                value={selectedObjectiveId}
                onChange={(e) => setSelectedObjectiveId(String(e.target.value))}
                displayEmpty
                sx={{
                  background: 'rgba(0,0,0,0.70)',
                  borderRadius: '6px',
                  border: '1px solid #868686',
                  color: '#FDFDFD'
                }}
              >
                <MenuItem value="">
                  <em>Please Select</em>
                </MenuItem>
                {objectivesUserCanAdd.length === 0 ? (
                  <MenuItem value="" disabled>
                    No objectives available
                  </MenuItem>
                ) : (
                  objectivesUserCanAdd.map((obj: any) => {
                    const optionValue =
                      getObjectiveIdentifier(obj) || (typeof obj === 'string' ? obj : obj?.objective || obj?.name);
                    const optionLabel = typeof obj === 'string' ? obj : obj?.objective || obj?.name || 'Objective';
                    if (!optionValue) return null;
                    return (
                      <MenuItem key={`${optionValue}-${optionLabel}`} value={optionValue}>
                        {optionLabel}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </Box>

            <Box display="flex" gap={1.5} justifyContent="center">
              <Button
                fullWidth
                onClick={() => {
                  setObjectiveModalOpen(false);
                  setSelectedObjectiveId('');
                }}
                sx={{
                  px: 1.75,
                  py: 1.25,
                  background: 'var(--Fill-Button-button-basic-default, #1C1C1C)',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '6px',
                  color: 'var(--Stroke-Button-button-basic-default, #FDFDFD)',
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                disabled={!selectedObjectiveId || savingObjective}
                sx={{
                  px: 1.75,
                  py: 1.25,
                  background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                  borderRadius: '6px',
                  color: 'var(--Stroke-Button-button-cta-default, #FDFDFD)',
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&.Mui-disabled': buttonDisabledSx
                }}
                onClick={handleAddObjective}
              >
                {savingObjective ? 'Adding...' : 'Add'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <RecruitNewMentor
        open={recruitModalOpen}
        onClose={() => setRecruitModalOpen(false)}
        title={`Find a ${drawerRole === 'mentee' ? 'mentee' : 'mentor'}`}
        mentorsList={[]}
        onProfileClick={() => {}}
        onChatClick={(each) => handleStartConversation(each)}
        onCalendarClick={() => {}}
        objId={recruitObjId}
        grpId={learningGrpId}
        apiType={`${drawerRole === 'mentee' ? 'mentor' : 'mentee'}`}
        learningObjective={createdLearningObjectivesList.find((lo: any) => lo.id === recruitObjId)}
      />
    </Box>
  );
}
